// Wellness Resources Content - Structured for clean display

export const wellnessContent = {
  'all-healing-self-healing': {
    id: 'all-healing-self-healing',
    title: 'All Healing Is Self Healing',
    subtitle: 'Why the Body Holds the Blueprint',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/n4ayn2x6_All%20Healing%20Is%20Self%20Healing%20%283%29.pdf',
    introduction: "Healing is not something done to the body, but something that arises from within it. This perspective is ancient, echoing through Chinese medicine, ancient movement systems, and lived human experience across cultures. The body inherently knows how to heal. The role of intervention is to remove what interferes with this natural process. At its core, all healing is self-healing.",
    designedForFlow: {
      title: "The Body Is Designed for Flow",
      icon: "logo",
      intro: "The body is not a machine of separate parts, but a living system of movement, communication, and circulation.",
      chineseMedicine: "In Chinese medicine, health is defined by flow: Qi moves through meridians, blood follows Qi, fluids follow blood, and consciousness follows all of it.",
      insight: "Flow is life expressed.",
      signals: "Pain, dysfunction, and illness are signals that something has stopped moving correctly—not enemies to be fought."
    },
    stagnation: {
      title: "Stagnation: The Root of Dis-Ease",
      icon: "logo",
      intro: "Stagnation arises when circulation is compromised.",
      process: [
        "Energy that doesn't move becomes stagnant",
        "Stagnation thickens",
        "Thickened fluid pools",
        "Pooling creates pressure, heat, or collapse"
      ],
      systems: [
        { name: "Qi stagnation", effect: "Affects organs and emotions" },
        { name: "Blood stagnation", effect: "Creates congestion and pain" },
        { name: "Lymphatic stagnation", effect: "Contributes to toxicity and inflammation" },
        { name: "Cerebrospinal fluid stagnation", effect: "Dulls vitality, perception, and regulation" }
      ],
      compensation: "At first, the body compensates quietly with tightness, fatigue, subtle discomfort, or emotional flatness. Over time, if stagnation persists, the body communicates more clearly. The system is asking for movement to be restored—not indicating something is broken."
    },
    organsIntelligence: {
      title: "Organs as Living Intelligence",
      icon: "logo",
      intro: "Organs are intelligent participants in the whole, not isolated objects. Each organ system has physiological roles, emotional expressions, movement patterns, and timing rhythms.",
      communication: "Organs communicate continuously with each other. With present flow, organs self-regulate with remarkable precision.",
      symptomLocation: "Symptoms often appear far from their source because the body adapts until it can no longer bear the load. The last place a symptom shows up is rarely the original problem.",
      healingFocus: "Healing requires listening for where flow was interrupted—not just treating the discomfort's location."
    },
    interruptsFlow: {
      title: "What Interrupts Flow?",
      icon: "logo",
      intro: "Energy flow is influenced by experience, stress, emotion, and thought.",
      thoughtsBeliefs: {
        title: "Thoughts and Beliefs",
        points: [
          "Repeated thoughts create repeated pathways in the body",
          "Beliefs shape posture, breath, and muscular tone",
          "When perception becomes rigid, the body organizes around that rigidity",
          "Flexibility of mind allows flexibility of tissue",
          "Chronic vigilance narrows circulation",
          "Control replaces responsiveness—flow requires options",
          "Fixed narratives reduce options and flow"
        ]
      },
      unprocessedEmotion: {
        title: "Unprocessed Emotion",
        points: [
          "Emotion is energy designed to move and resolve when felt and completed",
          "When suppressed or judged unsafe, emotion settles into the body",
          "Grief, anger, fear, and worry do not disappear when ignored—they become held patterns",
          "These patterns alter circulation, tension, and organ function over time"
        ],
        process: "What is unfelt becomes stored. What is stored becomes stagnant. What is stagnant eventually becomes symptomatic."
      },
      trauma: {
        title: "Trauma and Protective Stillness",
        intro: "Trauma is defined not by the event itself, but by the lack of completion of the response to a threat.",
        freeze: "When the nervous system cannot respond to threat, it chooses stillness ('freeze'). This is an intelligent survival strategy, not dysfunction. Energy pauses to preserve life.",
        problem: "Problems emerge when the body never receives the signal that safety has returned. In this state, expansion feels dangerous, circulation feels overwhelming, and rest feels unfamiliar.",
        key: "Healing cannot occur where safety has not yet been established."
      }
    },
    whyHealingDoesntWork: {
      title: "Why Healing Sometimes Doesn't Seem to Work",
      icon: "logo",
      intro: "Some people don't feel the effects of practitioners' work—not because the work is ineffective or the person is resistant, but because the system may not feel safe enough to reorganize.",
      survival: "If stillness has been the body's survival strategy, increased flow can feel threatening.",
      nervousSystem: "The nervous system filters every intervention through the question: 'Is it safe to change?'",
      integration: "Until the answer is 'yes,' healing efforts may pass through without integration. The body isn't rejecting healing—it's waiting for readiness."
    },
    roleOfGroundedWellness: {
      title: "The Role of Grounded Wellness",
      icon: "logo",
      objective: "Grounded Wellness does not aim to 'fix' the body. It creates conditions for safety, awareness, and circulation to return.",
      methods: "Through movement, breath, touch, presence, and education—we gently remove obstacles to flow and help the system feel supported enough to soften, unwind, and reorganize itself.",
      principles: [
        "The practitioner guides",
        "The body decides",
        "Healing unfolds in layers"
      ],
      approach: "This approach honors timing, capacity, and the deep intelligence of the nervous system."
    },
    healingAsPermission: {
      title: "Healing as Permission, Not Effort",
      icon: "logo",
      intro: "Self-healing is not about doing more—it's often about doing less.",
      less: "Less bracing, less forcing, less overriding sensation and intuition.",
      permission: "When the body feels permission to rest, feel, and move at its own pace, circulation resumes naturally. Fluids move, tissues hydrate, systems communicate again.",
      key: "Healing is not an act of will—it is a state of allowance."
    },
    returnToIntelligence: {
      title: "A Return to Inner Intelligence",
      icon: "logo",
      intro: "Every living system knows how to organize itself when interference is reduced. Seeds grow, wounds close, bones knit.",
      foundation: "Grounded Wellness is rooted in remembering this truth. Healing cannot be given or forced—it can only be supported.",
      blueprint: "All healing is self-healing, because the body has always carried the blueprint.",
      practitionerRole: "The work is not to control the body, but to listen closely to where it stopped moving and gently help it begin again."
    },
    organEmotions: {
      title: "The Organs as Emotional & Energetic Intelligence",
      icon: "logo",
      intro: "In Chinese medicine, organs are understood as centers of emotion, energy, and perception.",
      organs: [
        { organ: "Liver", energy: "Flow, direction, vision", balanced: "Motivation, creativity", stagnant: "Anger, frustration" },
        { organ: "Heart", energy: "Circulation, connection", balanced: "Joy, clarity", stagnant: "Anxiety, restlessness" },
        { organ: "Spleen", energy: "Nourishment, grounding", balanced: "Stability, trust", stagnant: "Worry, overthinking" },
        { organ: "Lungs", energy: "Breath, rhythm", balanced: "Acceptance, inspiration", stagnant: "Grief, sadness" },
        { organ: "Kidneys", energy: "Vitality, essence", balanced: "Courage, resilience", stagnant: "Fear, insecurity" },
        { organ: "Stomach", energy: "Receiving, processing", balanced: "Comfort, satisfaction", stagnant: "Disgust, anxiety" },
        { organ: "Small Intestine", energy: "Discernment, clarity", balanced: "Insight, discernment", stagnant: "Confusion, overwhelm" },
        { organ: "Large Intestine", energy: "Release, boundaries", balanced: "Letting go, resolution", stagnant: "Holding on, rigidity" },
        { organ: "Gallbladder", energy: "Decision, action", balanced: "Confidence, initiative", stagnant: "Indecision, bitterness" },
        { organ: "Bladder", energy: "Storage, release", balanced: "Ease, adaptability", stagnant: "Tension, fear" }
      ],
      key: "When energy moves, emotions flow. When emotions flow, healing becomes possible."
    },
    books: [
      { title: "The Body Keeps the Score", author: "Bessel van der Kolk" },
      { title: "The Biology of Belief", author: "Bruce Lipton" },
      { title: "Eastern Body, Western Mind", author: "Anodea Judith" },
      { title: "Waking the Tiger: Healing Trauma", author: "Peter A. Levine" }
    ]
  },
  'living-with-illness': {
    id: 'living-with-illness',
    title: 'Living with Illness & Dis-ease',
    subtitle: 'A Guide for Challenging Times',
    topNote: 'This resource is for anyone struggling in their current circumstances',
    image: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/p7hpolej_woman-walking-through-forest-with-backpack_153912-282767.avif',
    introduction: "Whatever life experience has brought you to this resource, I'm sorry for your challenging time. Go gently with yourself—everyone experiences setbacks very differently. The below is largely from my personal experiences, combined with my education and working with many clients over the years.",
    personalStory: {
      title: "My Story",
      content: "My experience stems from ovarian cancer at age 35, devastatingly diagnosed post-surgically. Prior to surgery, I believed it was a simple cyst. Four weeks after massive abdominal surgery, I was informed it was stage 1 cancer. They said I was 'fine' and could simply move on with my life. But I felt like I had been hit by a Mack truck. As a personal trainer and health professional, how could 'I' have cancer? It knocked my socks off and threw me into a massive identity crisis."
    },
    keyInsights: [
      {
        source: "Chinese Medicine",
        quote: "No disease short life, one disease long life.",
        explanation: "If you're delusional enough to believe you have no disease, you may not care for yourself appropriately. Those diagnosed with a disease tend to care for themselves knowing they need to—and may enjoy a longer life."
      },
      {
        source: "Buddhism",
        quote: "ALL FRUSTRATION is the NON ACCEPTANCE of what IS.",
        explanation: "Just sit with this for a moment."
      },
      {
        source: "Hippocrates",
        quote: "It is more important to know what sort of person has a disease than what sort of disease a person has.",
        explanation: "Rather than working with the disease itself, it's best to work with the person."
      }
    ],
    sixFoundations: {
      title: "The 6 Foundations",
      subtitle: "Tend to basic needs first, start simple and practical",
      icon: "logo",
      items: [
        { name: "Breathing", icon: "🌬️" },
        { name: "Thinking", icon: "🧠" },
        { name: "Movement", icon: "🚶" },
        { name: "Sleep/Rest", icon: "😴" },
        { name: "Nutrition", icon: "🥗" },
        { name: "Hydration", icon: "💧" }
      ]
    },
    timelineGuidance: {
      title: "Give It 1 Year",
      icon: "logo",
      content: "We heal in stages: 3 months, 6, 9, 12. At the 1-year mark there's this invisible, magical door that creates a shift—a feeling of enough distance from 'harm' that we settle in, feel freer, more healed. So do the work, pay attention, eat well, give yourself some down days, but mostly do the work of healing. 1 year doesn't mean you'll be healed—it's unadvisable to check in too frequently as it can be demotivating."
    },
    stagesOfHealing: {
      title: "The Stages of Healing",
      subtitle: "These are really the stages of grief—not linear, but we cycle through all of them",
      icon: "logo",
      stages: [
        {
          name: "Denial",
          description: "Shock, disbelief, numb feelings, robotic mode"
        },
        {
          name: "Blame (Self & Others)",
          description: "'I should have...', 'Why didn't I...', blaming circumstances, G-d, universe. Involves pain, guilt, remorse, resentment."
        },
        {
          name: "Anger/Indignation",
          description: "Bargaining, turning away from support, aggression, mood swings, depression, low tolerance, higher reactivity"
        },
        {
          name: "Survival",
          description: "Can look like depression, apathy, lackluster, passionless, just moving through life, languishing"
        },
        {
          name: "Integration",
          description: "Integrating the situation with our emotions. Many of us are emotion-avoidant. Let the emotions seep out."
        },
        {
          name: "Synchronicity & Serendipity",
          description: "Acceptance and hope. Beginning to understand why this was in our life, what we've learned, how we've grown."
        }
      ]
    },
    practicalGuidance: [
      {
        title: "Lowered Expectations = Increased Contentment",
        icon: "logo",
        content: "What you can accomplish now is different than it was. Your health IS your focus, your job. Healing is very hard work, but not the elbow grease kind—probably harder than anything you've ever done. How do you 'rest harder'? You can't! You'll figure out how to rest effectively. Restoration and rejuvenation are different than doing nothing."
      },
      {
        title: "Exercise & Movement",
        icon: "logo",
        content: "Sometimes we need to 'work out' and sometimes it's best to 'work in'—building energy rather than expending it. If you feel 6/10 or lower, use gentle movement to generate energy. Match your exercise to your 1-10 scale: feeling 1/10 = rest and sleep. Feeling 3/10 = light walk, 30% effort. You'll make many mistakes—this helps build your blueprint."
      },
      {
        title: "Create Healing Habits",
        icon: "logo",
        content: "Motivation will take a hike during this time. Just know that! Develop routines you logically understand are good for you: going to bed on time, resting, breathing, healthful foods, abandoning criticism, positive affirmations, compassionate self-talk. Much of the motivation for these things will elude you."
      },
      {
        title: "Build Your Team",
        icon: "logo",
        content: "You need support! Find it, build it, somehow, do it. Call friends, family, practitioners. Complementary medicine—acupuncturists, yoga practitioners, massage therapists, counselors, coaches, energy workers. Use medical AND complementary systems in tandem."
      }
    ],
    mindfulnessSection: {
      title: "Meditation & Mindfulness",
      icon: "logo",
      intro: "Even if you feel meditation is not for you—consider it. People think it's not for them because they can't 'quiet' their minds, but that's not really the point. It's tapping into the power of self-guided healing.",
      attitudinalFactors: {
        title: "7 Attitudinal Factors (MBSR)",
        note: "It helped me understand that 'Letting Go' is the 7th step in a 7-step process!",
        factors: [
          "Non-judging",
          "Patience",
          "A beginner's mind",
          "Trust",
          "Non-striving",
          "Acceptance",
          "Letting Go"
        ]
      }
    },
    emotionsSection: {
      title: "Working with Emotions",
      icon: "logo",
      intro: "Most of us were not taught to work with our emotions healthfully. Our emotional states were typically inconvenient for our parents. As a result, many of us have become emotion-phobic.",
      tool: {
        title: "Try This Tool",
        description: "Allow everything to be. No justification, no defending. Just a factual account. Even if it feels dysfunctional—write it down.",
        steps: [
          "Part of me feels ___________",
          "Of course part of me feels ___________",
          "Part of me has every right to feel ___________ because ___________"
        ],
        example: [
          "Part of me feels lost",
          "Of course part of me feels lost",
          "Part of me has every right to feel lost because my life got turned upside down this year"
        ],
        tip: "Go 20 times. The more you do this, you shed away protective delusions and get to what's really bothering you. Motto: We are NEVER upset about what we think we are upset about."
      }
    },
    metaphysics: {
      title: "Consider Metaphysics",
      icon: "logo",
      intro: "Metaphysics is the study of the messages of the body. The body does not lie. Pain, stuck energy, disease, injury—all carry messages of healing. Our bodies carry everything we could 'not process cognitively' at the time of the event.",
      examples: [
        { area: "Neck pain", meaning: "Not seeing someone else's point of view" },
        { area: "Sciatic/butt pain", meaning: "'You're a pain in my butt'—questioning value systems" },
        { area: "Elbow pain", meaning: "Difficulty with life changing direction" },
        { area: "Arthritis", meaning: "Autoimmune—how am I attacking myself?" },
        { area: "Low back pain", meaning: "Strained relationship with money" },
        { area: "Spinal problems", meaning: "Spine = support, not feeling supported by life" }
      ],
      note: "We don't have to 'fix' anything with Metaphysics. The body is simply conveying information—listening and considering is often the start of beautiful healing!"
    },
    energySection: {
      title: "Clearing Your Space & Energy",
      icon: "logo",
      items: [
        "Beware of toxic people, news, newspapers, TV shows",
        "One episode of news may radiate in your mind for hours or days",
        "Consider getting your news elsewhere or take a news break—the world is not your problem right now",
        "Surround yourself with understanding, caring people",
        "TV is not restorative—nothing wrong with zoning out, just know it isn't rejuvenating",
        "When you lay on the floor with your body, walk in nature, chat with a supportive friend—you feel better"
      ]
    },
    quickReference: {
      title: "Keep This Close",
      subtitle: "For the mind, heart, and maybe the refrigerator door",
      icon: "logo",
      items: [
        { tip: "Eat well", detail: "Consider food as medicine. Health-giving foods 80%, soul foods 20%" },
        { tip: "Frequency/music therapy", detail: "YouTube healing frequencies while resting or sleeping" },
        { tip: "Joy and pleasure", detail: "Do things daily that elicit good feelings. The fastest way to change brainwaves is through curiosity." },
        { tip: "Touch or energy therapy", detail: "Find practitioners that work for you" },
        { tip: "Reading or quiet time", detail: "Self-help, healing, or good old fiction—spending time with YOU" },
        { tip: "Juicing and lemon water", detail: "Alkalize the body—a healing environment" },
        { tip: "Clear your space and energy", detail: "Be mindful of your environment and anything taxing your energy" }
      ]
    },
    quotes: [
      "It is okay if it takes time for this mountain to be climbed. — Morgan Harper Nichols",
      "You can't save time. You can only spend it. But you can spend it wisely or foolishly. — Benjamin Hoff, The Tao of Pooh",
      "If you can FEEL it, you can HEAL it."
    ],
    books: [
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "The Wild Edge of Sorrow", author: "Francis Weller" },
      { title: "Life on the Refrigerator Door", author: "Alice Kuipers" },
      { title: "The Impersonal Life", author: "Joseph Benner" },
      { title: "You Can Heal Your Life", author: "Louise Hay" },
      { title: "Anything by Pema Chödrön", author: "(titles will speak to you)" },
      { title: "Anything by Maya Angelou", author: "" }
    ],
    closingThought: "Chances are if you are reading this, you are struggling to a degree. Make these recommendations work for you. You may have a minor or major challenge—either way, you will find your way through. It is how we are wired. You can do this!"
  },
  
  'try-not-to-lose-the-plot': {
    id: 'try-not-to-lose-the-plot',
    title: '"Try Not to Lose the Plot!"',
    subtitle: 'An Approach to Your Health in Difficult Times',
    image: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/hxxmppmp_writing-tips-3-dont-lose-the-plot.jpg',
    introduction: "Being Canadian, the first time I heard this expression, I had no idea what it meant! I was living in New Zealand and my roommate used it often—speaking about someone who appeared to have lost their cool in a 'crazy' sort of way. These thoughts on health may seem unorthodox compared to the usual 'perfect pushing' of mainstream media. The reality is, in difficult times, tending to our health appropriately may feel near impossible.",
    keyMessage: {
      title: "The Core Truth",
      icon: "logo",
      content: "Something has to give—and you don't want it to be your sanity! Allow certain things to be less than perfect. Decide the most important areas and the non-negotiables. The operative word is SOMETHING rather than EVERYTHING. So all you all-or-nothing people out there... strap on your 'finding your middle' boots!"
    },
    survivalMode: {
      title: "Understanding Survival Mode",
      icon: "logo",
      content: "As humans, we have this wonderful mode called Survival Mode—it's incredible! We have this innate ability and drive to survive. And we can get stuck there, feeling like we're stagnating or going backwards. But I have proof this isn't true! Backwards doesn't exist—it's a construct of your mind. Only forward exists, just look at your toes!",
      keyInsight: "During these times, working on self-compassion and self-kindness is key! For many years I believed kicking my ass was the way. As I acquire wisdom, I now understand this is not the way. The way is love and compassion for self. Unconditional. The very thing we crave from others and yet struggle to give to ourselves."
    },
    exerciseInsight: {
      title: "A Word About High-Intensity Exercise",
      icon: "logo",
      intro: "Many people turn to high-intensity exercise during tough times to 'burn off steam'. While this may be effective for some, consider what happens when we use one type of stress to cope with another type of stress—managing aggression with aggression.",
      story: {
        title: "A Client's Story",
        content: "I worked with a high-powered executive who would come in literally ready to 'kill someone.' We'd throw medicine balls, whack things, jump, climb, run. Did he return to the office calmer? Yes! But tomorrow was the same deal. Then he hurt his back—major injury, 500-day recovery. He had no choice but to find another way. We did incredibly gentle exercises called Feldenkrais, which sometimes looks like napping. Within 10 minutes of 'bliss exercises,' he was a different man. Eventually, he could calm himself during meetings on his own. He's back to high-intensity exercise when he wishes, rather than it feeling like a compulsion."
      },
      takeaway: "It took about 500 days. It's a process—re-training and re-patterning the mind, the body, and the Nervous System."
    },
    coreGuidance: [
      {
        title: "Forget Big Changes",
        icon: "logo",
        content: "In times of crisis is NOT the time for big changes. Learn and add 1 or 2 things that help and are easy to manage. Many people abandon programs during crisis, leaving them feeling worse. Very few people are ever honest about why they're adopting a healthier routine—it's often laced with shame ('I am not enough the way I am')."
      },
      {
        title: "Add In, Don't Take Out",
        icon: "logo",
        content: "Try adding things in—forget about taking things out. Some people try to stop drinking, eating sugar, or smoking during crisis. Stop it! It's like taking a baby's blankie away during a tantrum. Don't worry about comforts—we need comfort! Give yourself permission to self-soothe. If we add in good stuff, nourishing stuff—healthy proteins, maybe juicing to pack in nutrients, or a power smoothie to get all your needs in—treats are going to be ok. Fill yourself when you can with the nourishing stuff... and that squeezes out the less healthy stuff anyway. So that treats are ok and soothing vs destructive."
      },
      {
        title: "Decide Your Non-Negotiables",
        icon: "logo",
        content: "For me it's sleep. I cannot let my sleep suffer. If it does, everything else gets worse. Once you figure out your priorities and truly make them the priority, give yourself permission to put you first and shed away unnecessary tasks, events, and to-do's."
      }
    ],
    addIns: {
      title: "Great Add-Ins",
      icon: "logo",
      items: [
        { tip: "Fresh green juices", detail: "Will flood your system with nutrients and enzymes (not store-bought/pasteurized)" },
        { tip: "More sleep", detail: "Chances are you require more sleep than usual at these times" },
        { tip: "Calming practice", detail: "Guided meditation, self-help books, music, gentle stretching, walking, nature, breathing. Even 10 mins a day can be monumental!" },
        { tip: "Connection time", detail: "Find time with the 'right people for right now.' You are allowed to choose who you see." },
        { tip: "Laughter", detail: "Nuff said" },
        { tip: "Honour yourself", detail: "If you are with us, you are doing more right than wrong—batting 1000!" },
        { tip: "Constructive coping", detail: "Turn to the pros. Books if therapy isn't in your economics. YouTube has plentiful free resources!" },
        { tip: "Patience", detail: "Patience has a nickname for a reason—because it's difficult! That nickname is Virtue." },
        { tip: "Gratitude", detail: "Always helpful, even if difficult. Do it anyway, even once a week." },
        { tip: "Boundaries", detail: "Tough, but necessary at these times." },
        { tip: "Forgiveness", detail: "For when you forget, get overwhelmed, or flake in front of the TV with doritos and ice cream. It's ok, it really is." },
        { tip: "Knowing", detail: "This is not forever. This too shall pass. This is unlikely your first crisis—you've done this before. You WILL move through this." }
      ]
    },
    thingsToToss: {
      title: "Things to Toss",
      subtitle: "If you're taking things out, consider these:",
      icon: "logo",
      items: [
        "Anyone not in your corner (even just for now)",
        "Perfectionism",
        "The ideal",
        "Overachieving, overworking",
        "High expectations",
        "Bright lights at night (for sleep)",
        "Self-punishment and self-beratement",
        "The harsh voice of the Inner Critic"
      ],
      innerCriticNote: "You will never really quiet this voice or get rid of it! You can choose not to listen—this takes years of training. I substituted many voices for my inner critic through books by authors with much kinder, gentler voices than my own: Jon Kabat-Zinn, Pema Chödrön, and most importantly—Winnie the Pooh! Tough to take harsh criticism seriously from a gentle voice that sounds like Pooh Bear!"
    },
    askForHelp: {
      title: "Ask For Help",
      icon: "logo",
      content: "Contrary to popular belief, this is a sign of strength rather than weakness. And then forgive yourself if you decide to pull a stiff upper lip and go it alone anyway."
    },
    quotes: [
      "You can't save time, you can only decide how you wish to spend it. — Winnie the Pooh",
      "Something has to give—and you don't want it to be your sanity!",
      "The way is love and compassion for self. Unconditional."
    ],
    books: [
      { title: "The Tao of Pooh", author: "Benjamin Hoff" },
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "Anything by Pema Chödrön", author: "" }
    ],
    closingThought: "We're attempting to be kind to ourselves. If we considered leaving the coping mechanism in and taking out the self-punishment surrounding it... maybe we stand a chance at moving through less scathed. You are amazing—we just forget in times of crisis."
  },

  'adrenal-support': {
    id: 'adrenal-support',
    title: 'Adrenal Support',
    subtitle: 'Restoring Balance to Your Stress Response',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
    introduction: "Most of us are somewhere in Level 1-4 adrenal fatigue. Our lifestyle, technology, fast-paced life and multitasking have all depleted our human body that is not designed to live like a robot. Adrenal fatigue is an early sign that your body's homeostasis (efficient and effective functioning) is off.",
    keyMessage: {
      title: "The Good News",
      icon: "logo",
      content: "Dis-ease is the stage before Disease. This sign is a call to pay attention. The next stage could be disease and you have time to right the ship."
    },
    mainSteps: [
      {
        number: "1",
        title: "Sleep",
        icon: "logo",
        content: "The most important step is to restore sleep. Without proper sleep your body will have a difficult time finding its equilibrium. Cortisol (our stress hormone) is the most important to get back on track.",
        tips: [
          "Sleep in blackout room with no technology or EMF",
          "Wake daily at the same time, preferably with sun",
          "Go to bed early — 10pm",
          "Low lights in evening time",
          "Develop a stress-relieving evening routine"
        ]
      },
      {
        number: "2",
        title: "Electrolytes in Early AM",
        icon: "logo",
        content: "Vega is a great brand with loads of good stuff. Coconut water can work also. No Powerade or Gatorade — the balance isn't right, too much sugar. High quality pink salt in water adds all minerals."
      },
      {
        number: "3",
        title: "Bone Broth",
        icon: "logo",
        content: "Consider bone broths daily or as often as possible, best on empty stomach. Helps add healing and nourishing back into the system, aids hydration, adds collagen to soothe and heal gut lining, and is high in amino acids — the body's building blocks."
      },
      {
        number: "4",
        title: "Coconut Oil or MCT Oil",
        icon: "logo",
        content: "2 tbsp for women and 3 for men is the therapeutic dosage. That's right — tablespoons! Saturated fats are the precursors for our healthy hormone system."
      },
      {
        number: "5",
        title: "Adrenal Support Supplements",
        icon: "logo",
        content: "ADRENAsmart or other adrenal combinations. Try Cortisol Manager from Integrative Therapeutics.",
        supplements: ["Holy Basil", "Ashwagandha", "Vit D", "Vit ADEK", "Adaptogens", "Magnesium (esp L-Threonate)", "Chamomile teas", "Lemon balm", "Rosemary", "Valerian"]
      },
      {
        number: "6",
        title: "ME (Body) Time",
        icon: "logo",
        content: "10-15 mins of blood-moving movements. Just like walking the dog makes the dog feel valued by the master, the body is the same. Gentle stretching, yoga, breathing, rolling — what matters is the master respects the body that carries them around all day."
      },
      {
        number: "7",
        title: "ME Rituals",
        icon: "logo",
        content: "Create ME rituals specifically, or if time is precious, take things you're already doing and make them into rituals. Slow them down, spend more time with them, make them valuable. ME rituals are conscious and deliberate — not zoning out, but tuning in."
      },
      {
        number: "8",
        title: "Nutrition",
        icon: "logo",
        content: "During down times we go for soul food — because our soul probably needs it! Be mindful to get all nutrients possible. Lots of colorful veg, fresh juices, healthful proteins and fats. Soul foods are fine until they become more than 20% of your intake."
      }
    ],
    cortisolGuide: {
      title: "Reducing Cortisol Quick Guide",
      icon: "logo",
      items: [
        "Limit caffeine — remove sugar, no coffee and sugar together",
        "Consider adding fat to coffee (bulletproof style)",
        "Limit sugar as much as possible",
        "Develop a gentle AM routine — stretches, hot water with lemon",
        "Avoid skipping meals — your body craves predictability",
        "Be prepared with snacks (fat, protein, and carbs together)",
        "Avoid eating carbs on their own — spikes blood sugar",
        "Hydrate — try structured water (cobalt blue glass bottle in sun for 1 hr)"
      ]
    },
    vitaminsAndMinerals: {
      title: "Vitamins + Minerals",
      icon: "logo",
      items: [
        { name: "Vitamin B5 (Pantothenic Acid)", benefit: "Balances cortisol secretions" },
        { name: "Vitamin B6 (Pyridoxine)", benefit: "Restores calm to the nervous system" },
        { name: "Vitamin C", benefit: "Required for stress hormone production" },
        { name: "Vitamin E", benefit: "Neutralizes damaging free radicals in adrenal glands" },
        { name: "Calcium", benefit: "Produces calming effects; restores cortisol levels" },
        { name: "Magnesium", benefit: "Helps cellular energy; promotes muscle relaxation + sleep" },
        { name: "Trace minerals", benefit: "Zinc, manganese, selenium, iodine support hormonal balance" }
      ]
    },
    adaptogens: {
      title: "Adaptogenic Herbs + Plant Compounds",
      icon: "logo",
      items: [
        { name: "Astragalus", benefit: "Bolsters immune system + reduces inflammation" },
        { name: "Cordyceps", benefit: "Stabilizes blood sugar, supports immune response" },
        { name: "Eleuthero", benefit: "Shortens recovery from acute stress" },
        { name: "L-Theanine", benefit: "Increases alpha waves, improves mood and calm" },
        { name: "Rhodiola", benefit: "Reduces fatigue and brain fog; boosts mood" },
        { name: "Passion Flower", benefit: "Calms stress, eases restlessness, improves sleep" },
        { name: "Phosphatidylserine", benefit: "Blocks harmful effects of cortisol" }
      ]
    },
    closingThought: "There are more options — this can give you a good start. Remember: the body responds to consistency and care over time."
  },

  'thumb-rules-wellbeing': {
    id: 'thumb-rules-wellbeing',
    title: 'Great Thumb Rules Towards WellBeing',
    subtitle: 'Small Changes, Big Impact',
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800',
    introduction: "Within food choices, sometimes small and minor modifications can have a big impact. Here are some small and maybe a few big changes. Especially see the Meat Buying Guide for all the important trickery and foolery that exists today with our food system.",
    foodGuidelines: [
      {
        title: "Veg and Fruit",
        icon: "logo",
        content: "More vegetables! If you prefer cooked veg, go for it. Fresh fruit/vegetable juices (more veg than fruit) a few times per week will pack in nutrients without needing to eat a lot of vegetables."
      },
      {
        title: "Meats",
        icon: "logo",
        content: "Organic meats are free of added hormones and antibiotics, usually fed better and allowed to roam free. Rotating these through can be very important, especially for hormone rebalancing."
      },
      {
        title: "Salt",
        icon: "logo",
        content: "Sea salt or Himalayan (pink) salt only. This salt is so good for you that it's an easy way to mineralize or re-mineralize your body and provide many base resources we need to live well."
      },
      {
        title: "Nuts",
        icon: "logo",
        content: "Raw nuts only! The natural oil in roasted nuts has gone rancid from industrial heat. Putting your own light toast on nuts is fine for extra flavor."
      },
      {
        title: "Fat Sources",
        icon: "logo",
        content: "Very important for hormonal health: butter (preferably raw, organic), coconut oil, raw olive oil, raw nuts and seeds, avocado. ONLY these! Canola oil is toxic — avoid at all cost. All commercially produced seed oils are incredibly toxic."
      },
      {
        title: "Grains",
        icon: "logo",
        content: "Steel cut oats are much better than quick cook (eat with lots of cinnamon). Brown rice, quinoa are better choices. Anything quick cook or white rice is essentially like sugar to our bodies."
      },
      {
        title: "Cooking Methods",
        icon: "logo",
        content: "Try to avoid microwaves — they denature food and leave it inert (dead food, little nutrients). Low nutrient count can lead to cravings as your body looks for nutrients."
      },
      {
        title: "Organics",
        icon: "logo",
        content: "Include colorful fruits and veg at every meal. Choose organics whenever possible to get more nutrients and eliminate pesticides/herbicides that contribute to belly fat and hormonal imbalances."
      },
      {
        title: "Sugar and Starches",
        icon: "logo",
        content: "Understanding how sugars and starches act in the body will start to regulate hormonal responses, starting with Insulin then Cortisol, which helps balance weight and hormones."
      }
    ],
    lovePhilosophy: {
      title: "Love Philosophy and Gentle Attention",
      icon: "logo",
      content: "Beware of discipline, impatience and punishment. When in doubt choose The Love Philosophy. If you hate a body part or have anger towards the body... the body will not love you back. Health can be complicated and slow to change. Patience, gentle determination toward WellBeing — it is or can be a treacherous path. Go gentle with self and keep your eye on the long ball."
    },
    meatGuide: {
      title: "Meat Shopping — Decoding Buzz Words",
      icon: "logo",
      intro: "There's lots of doom and gloom documentaries about the horrible-ness of our food system. The dilemma is how to feed the world and practices are getting weird. I like to be informed, talk to local farmers, read labels.",
      helpful: {
        title: "Helpful Buzz Words",
        items: ["100% grass fed", "Grass finished", "Free-range", "Free-roaming", "Pasture raised", "Ethically raised"]
      },
      misleading: {
        title: "Misleading Buzz Words",
        note: "Still helpful, but not all it's cracked up to sound like",
        items: [
          { term: "Grass fed", explanation: "80% of life is grass fed, but other 20% is unknown" },
          { term: "Antibiotic/hormone free", explanation: "Great aspect, but no idea what else is happening" },
          { term: "Organic", explanation: "Likely means animals fed organic grains — still not their natural diet" }
        ]
      },
      unhelpful: {
        title: "Unhelpful Buzz Words — Avoid!",
        items: [
          { term: "Vegetarian fed", explanation: "Weird! Cement and cardboard are vegetarian too — it's trickery!" },
          { term: "No buzz words", explanation: "Means factory practices, usually unethical, antibiotics and hormones" }
        ]
      }
    },
    metabolicTyping: {
      title: "Metabolic Typing",
      icon: "logo",
      content: "I currently follow Metabolic Typing eating, which changed my life. It determines if you're a Protein, Mixed, or Carb type and what proportions suit you best. I think eating what YOU are supposed to eat is best. Ask me about the metabolic typing questionnaire — a simple way to get you started."
    },
    recommendations: {
      movies: [
        { title: "Fat, Sick and Nearly Dead", note: "Gets into blood sugar and crap food" },
        { title: "Cooked", note: "4-part Netflix series by Michael Pollan — a journey through food history" }
      ],
      books: [
        { title: "The Omnivore's Dilemma", author: "Michael Pollan" },
        { title: "In Defense of Food", author: "Michael Pollan" },
        { title: "Younger Next Year", author: "" },
        { title: "Stronger Next Year", author: "" }
      ]
    },
    closingThought: "If you follow the above, eat for YOU, eat with a conscience... I suspect you will be good!"
  },

  '8-weeks-better-sleep': {
    id: '8-weeks-better-sleep',
    title: '8 Weeks to A Better Sleep',
    subtitle: 'A Comprehensive Guide to Restoring Your Sleep',
    image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=800',
    introduction: "For some of us, sleep can be very elusive, either at times, far too often or always. I often share with clients that I feel like I was born WITHOUT the sleep gene! In my late 30's I 'fixed' my sleep. It was maybe a year after a major surgery for ovarian cancer. This whole thing sent me down a major rabbit hole! Not only physically, mentally, also emotionally!",
    personalStory: {
      title: "My Journey",
      content: "As a personal trainer and health practitioner, how could 'I' have cancer?! I had been quite tolerant to 'burning the candle at both ends' for the majority of my life—not just tolerant... almost proud! Until this surgery hit me like a Mac truck. I was a functioning insomniac for a good portion of University, worked in bars and shift work in the ER Dept. So my sleep was a complete disaster!"
    },
    traumaNote: {
      title: "A Word About Trauma",
      icon: "logo",
      content: "At times our sleep is a result of our childhood experiences. In order to sleep soundly, we need to feel safe and secure. If you grew up in an environment that wasn't safe, sleep challenges may stem from unresolved trauma. Sleep will never heal if the trauma disruption is not resolved. You may work tirelessly on these recommendations and find no sleep improvements until this is addressed."
    },
    cortisolScience: {
      title: "The Science of Sleep",
      icon: "logo",
      intro: "The most important thing to remember about sleep is that it's mostly controlled by cortisol. Anything that raises cortisol affects sleep: stress, sugar, caffeine, natural light, artificial light, screens, EMFs, and many medications.",
      codeFormula: "Light → Sunshine → Cortisol release → Daytime activities → 'I should be awake right now'",
      insight: "The time on the clock is irrelevant—your body responds only to chemical messengers.",
      normalCurve: "Notice that stress hormones start naturally dropping between 3pm-6pm! This is the little dip in energy we typically feel around 3-4pm. THIS IS GOOD AND HEALTHY! It's preparing us for restful slumber.",
      graphics: {
        normalCurve: {
          image: "https://customer-assets.emergentagent.com/job_hello-world-4550/artifacts/zajixb3e_stress%20normal%20curve.png",
          title: "Normal Anabolic/Catabolic Hormone Levels",
          caption: "A healthy rhythm shows cortisol (black line) peaking in the morning during 'Accelerating Activity', then declining through the day. The white line shows anabolic hormones rising at night for Physical and Psychological Repair."
        },
        exaggeratedCurve: {
          image: "https://customer-assets.emergentagent.com/job_hello-world-4550/artifacts/2vohr542_stress%20exagerrated%202.png",
          title: "Exaggerated Stress Response",
          caption: "When stress hormones remain elevated throughout the day (black line stays high), it cuts into our repair time. Notice how little room is left for Physical and Psychological Repair compared to the normal curve."
        }
      }
    },
    sleepHygieneTips: [
      {
        title: "Get to sleep by 10:30pm",
        icon: "logo",
        content: "We get a wave of healing hormones that repair muscle/bone, burn fat, and restore skin roughly between 11pm and 2am. Psychological repair occurs between 3am and 5am. You want to catch this wave!"
      },
      {
        title: "Minimize bright lights before bed",
        icon: "logo",
        content: "Avoid bright lights (especially fluorescent) for at least 2 hours before bed. Try candles, lamps, or Blue Light Blocking glasses."
      },
      {
        title: "Sleep in complete darkness",
        icon: "logo",
        content: "Any residual light and EMFs can raise cortisol, even an alarm clock light. Remove TVs, add blackout curtains. Unplugging is more effective than just turning off."
      },
      {
        title: "Avoid stimulants after lunch",
        icon: "logo",
        content: "Caffeine, alcohol, sugar, nicotine. The half-life of caffeine is ~5.5 hours. A 300mg coffee at 3pm means 150mg at 9pm and 75mg at 3am! As little as 30mg can prevent sleep."
      },
      {
        title: "Eat right for your type",
        icon: "logo",
        content: "Particularly at dinner. Taking in excessive carbs or proteins if your body isn't designed for it can cause stress. See Metabolic Typing."
      },
      {
        title: "Stay hydrated",
        icon: "logo",
        content: "Our bodies have very little water reserve. Once dehydrated, the body responds as though experiencing stress. Stress = awakening hormones = waking in the middle of the night."
      },
      {
        title: "Exercise wisely",
        icon: "logo",
        content: "Regular exercise aids stress reduction and burns cortisol. Heavy exercise signals the brain to enter deep sleep for repair. However, exercise after dinner may affect sleep—choose gentle, restorative exercise in the evening."
      },
      {
        title: "Clear electrical appliances",
        icon: "logo",
        content: "TVs, radios, lamps give off EMFs even when off. Position items far from your bed, or unplug them entirely."
      }
    ],
    hormonalPathways: {
      title: "The 3 Hormonal Pathways of Sleep",
      icon: "logo",
      intro: "Serotonin (neurotransmitter) is needed to create Melatonin (hormone). Tryptophan (amino acid) is needed to create Serotonin. They're all interrelated and regulated by the pineal gland.",
      pathways: [
        {
          name: "Tryptophan Pathway",
          icon: "1️⃣",
          description: "Best not to take tryptophan in pure form—give the body ingredients to 'bake' it.",
          supplements: ["5HTP 50-100mg, an hour before bedtime", "Magnesium Citrate (powder form as tea is best)", "B vitamins during day (for energy, NOT before bed)"]
        },
        {
          name: "Melatonin Pathway",
          icon: "2️⃣",
          description: "Works for roughly 30% of population. If this is your affected pathway, you'll sleep great and wake feeling rested. If not, you'll wake in the middle of the night feeling groggy."
        },
        {
          name: "Serotonin Pathway",
          icon: "3️⃣",
          description: "The trickiest pathway—related to joy, sleep, depression, mood. Best helped by joyful activities, laughter, pleasure. Depression dramatically impacts this. Sometimes therapy/coaching is needed to address root causes."
        }
      ]
    },
    otherSupplements: {
      title: "Helpful Additions",
      icon: "logo",
      note: "If taking medication, consult your pharmacist about interactions!",
      items: [
        "Sleep support teas: valerian, chamomile, hibiscus",
        "General wellness: Vit Bs, Vit D, Iron (during day)",
        "Complementary services: acupuncture, massage, yoga, Feldenkrais, meditation",
        "432 Hz frequency music (YouTube has plenty)",
        "Journalling, reading"
      ]
    },
    stressSection: {
      title: "The Role of Stress",
      icon: "logo",
      intro: "Stress and digestion are highly linked. The Parasympathetic (Rest/Digest) and Sympathetic (Fight/Flight) systems work like a see-saw. When one is active, the other is off.",
      goodNews: "1 hour in the Parasympathetic System can restore balance from a heavy day. It's roughly 1 hour to 8 hours—so just 1 hour daily of calming activities may make a world of difference!",
      framework: {
        title: "Eliminate, Uncover, Manage",
        steps: [
          { step: "Eliminate", content: "Can any stressors actually be eliminated? Have a tough look." },
          { step: "Uncover", content: "Hidden stressors: nutrient deficiency, dehydration, Candida, sleep debt, unfinished business, unspoken words, boundary issues." },
          { step: "Manage", content: "We can't get rid of all stress. Nourish self, manage body/mind/soul, find compassion." }
        ]
      }
    },
    selfCareTips: [
      { tip: "Lots of nutrients", detail: "The more nutrients, the more we can handle stress. We gobble up nutrients when stressed." },
      { tip: "Stress-relieving activities", detail: "Yoga, walking, meditation, baths, music, dancing, playing with children, reading (books, not iPad)." },
      { tip: "Nature time", detail: "Especially barefoot! Contact with earth brings natural rhythms back down." },
      { tip: "Mindfulness", detail: "Sit with your mind. Notice the chatter without changing it. Lend a compassionate ear to your own irrational ramblings." }
    ],
    bedtimeAdjustment: {
      title: "8-Week Bedtime Adjustment",
      icon: "logo",
      intro: "If you're a post-10:30pm bedtime person, move your bedtime forward slowly. You can't go from 1am to 10pm overnight—you'll wake at 2am with no sleep in sight!",
      method: "Move bedtime up 15-30 minutes per week. Bodies are slow to change.",
      example: "Week 1: 1:00am → 12:45am, Week 2: 12:45am → 12:30am... continue for 8 weeks"
    },
    books: [
      { title: "Mindfulness for Beginners", author: "Jon Kabat-Zinn" },
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "The Untethered Soul", author: "Michael Singer" },
      { title: "Making Friends with the Mind", author: "Pema Chödrön" }
    ],
    closingThought: "It may take many years to rectify many years of poor sleep habits—and that's ok. You will feel better every step of the way, and feeling better, feels better! I never really knew how it could feel to feel well-rested and alert."
  },

  'attainable-approach-stress': {
    id: 'attainable-approach-stress',
    title: 'An Attainable Approach to Reducing Stress',
    subtitle: 'Managing What We Can, Accepting What We Cannot',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
    introduction: "Have you ever tried to 'manage' all your stress? How did that go? It doesn't usually go well. Humans seem innately terrible at assessing our stressors—we tend to only look in 1 or 2 places. Plus, perfectionists like to 'appear' like we 'have it all together,' so exploring our stress often brings feelings of failure.",
    keyInsight: {
      title: "The Reality Check",
      icon: "logo",
      content: "We have a built-in Stress System—this is evolution! It saves us, allows us to move immovable objects. The problem is... THIS IS EXHAUSTING! And inevitably, another plate is thrown our way, or we drop one."
    },
    coreFramework: {
      title: "The 3-Step Approach",
      icon: "logo",
      intro: "We can't manage all the stress. When we try, we simply feel like failures. Consider applying this filter:",
      steps: [
        {
          number: "1",
          name: "Eliminate",
          question: "What can be eliminated?",
          content: "Is there/are there some stressors that can actually be eliminated? Have a tough look. If the answer is no, consider Roving Priorities—a time/energy prioritization to see if there's alignment.",
          icon: "✂️"
        },
        {
          number: "2",
          name: "Uncover",
          question: "What's hidden?",
          content: "Hidden stressors: nutrient deficiency, dehydration, Candida, sleep, unfinished business, unspoken words, boundaries, IMESP (Intuitive, Mental, Emotional, Spiritual, Physical). We all have things we've put on the backburner—might this be the time to address them?",
          icon: "🔍"
        },
        {
          number: "3",
          name: "Manage",
          question: "What can we control?",
          content: "We cannot possibly get rid of all stress. We can pay attention to ourselves, nourish self, manage body/mind/soul to cope more effectively, find compassion and functional self-support.",
          icon: "🎯"
        }
      ]
    },
    selfCareSection: {
      title: "Self-Care Essentials",
      icon: "logo",
      items: [
        { tip: "Nutrients", detail: "Limit sugars and CRAP food. More nutrients, less toxins will help the entire process." },
        { tip: "Emptying Activities", detail: "Yoga, meandering walking, meditation, baths, music, dancing, playing with children, reading books (not iPad)." },
        { tip: "Nature Time", detail: "As much as possible! Especially barefoot (grounding). The more contact with earth, the more it counters everyday stress." },
        { tip: "Frequency Healing", detail: "Look up Schumann Resonance on YouTube—an option when you can't get outside." },
        { tip: "Sitting with Discomfort", detail: "Explore being present with the chatter in our minds. Some call this mindfulness. It's very challenging but transformative." }
      ]
    },
    importantTruth: {
      title: "The Important Truth",
      icon: "💜",
      content: "You are NOT a failure if you can't HANDLE everything that is happening. If you don't feel like you're HANDLING things... maybe consider getting curious about how you are SUPPORTING yourself through these challenges."
    },
    reflectionQuestions: [
      "Are we being our own best friend?",
      "Are we functionally reparenting/supporting self?",
      "What is our relationship with stress/chaos? How might it serve part of us?",
      "Are we honouring our Emotions?"
    ],
    emotionsTool: {
      title: "Working Directly with Emotions",
      icon: "logo",
      intro: "Allow everything to be. No justification, no defending the feelings. Just a factual account.",
      template: [
        "Part of me feels ___________",
        "Of course part of me feels ___________",
        "Part of me has every right to feel ___________ because ___________"
      ],
      example: [
        "Part of me feels overwhelmed",
        "Of course part of me feels overwhelmed",
        "Part of me has every right to feel overwhelmed because I'm carrying more than I can handle right now"
      ]
    },
    parentingInsight: {
      title: "Understanding Our Inner Voice",
      icon: "logo",
      intro: "I often say: show me someone who is struggling and I'll show you someone who is being harsh with themselves. Our default is to be so harsh—partly from the perception of what was expected of us growing up.",
      parentingTypes: [
        { type: "Nurturing", description: "Light: supportive, encouraging. Shadow: enabling, over-protecting." },
        { type: "Critical", description: "Light: high standards, excellence. Shadow: never good enough." },
        { type: "Cynical", description: "Light: realistic, grounded. Shadow: dismissive, belittling." }
      ],
      reflection: "Get curious about how you were parented and how you model your internal voice. True healing stems from our desire to embrace our Magnificence and see ourselves as sacred creatures on a journey of wisdom."
    },
    books: [
      { title: "The Untethered Soul", author: "Michael Singer", note: "Best book for drawing attention to the voices in our heads" },
      { title: "Mindfulness for Beginners", author: "Jon Kabat-Zinn" },
      { title: "Making Friends with Your Mind", author: "Pema Chödrön" },
      { title: "The Tao of Pooh", author: "Benjamin Hoff" },
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "Love and Rage", author: "" }
    ],
    closingThought: "I had/have a mind with a very cynical edge. Understanding this was the key to transforming my stress levels and belief in myself. I still have this cynical inner critic—I always will—but perhaps I believe it less now."
  },

  'why-we-suck-at-recognizing-stress': {
    id: 'why-we-suck-at-recognizing-stress',
    title: 'Why We Notoriously Suck at Recognizing Stress',
    subtitle: 'Understanding Our Built-In Blind Spots',
    image: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/robu4fh7_image.png',
    introduction: "Most people don't realize they're stressed until they're exhausted, irritable, or their body starts to break down. It's not because we're weak — it's because of how the body, mind, and society are wired.",
    personalStory: {
      title: "A Lesson in Habituation",
      content: "Many years ago, I was travelling the Southern hemisphere. In Fiji, my dive instructor got his feet in the water and said 'brrrrr!' I laughed—the water was a balmy 28°C, I was coming from the frigid 11°C waters of NZ! Day 3, I arrived and said 'whoa, it's cold!' The water had dropped to 27°C! It took a mere 3 days for my body to acclimatize. This gives much insight into habituation—which has light and shadow sides, just like compensation."
    },
    eightReasons: {
      title: "Why Stress Flies Under the Radar",
      icon: "logo",
      reasons: [
        {
          number: "1",
          title: "The Body Adapts Too Well",
          content: "Our stress system was designed for short bursts, not constant modern pressures. When stress becomes ongoing, the body adjusts to the higher load and calls it 'normal.' You keep going, but your system is quietly wearing down."
        },
        {
          number: "2",
          title: "Stress Can Feel Like Energy",
          content: "Hormones like adrenaline and cortisol make us alert, focused, and productive. It feels like we're doing great—until we crash. That temporary boost masks the body's signal that it's in overdrive."
        },
        {
          number: "3",
          title: "We've Lost Touch With Our Body's Clues",
          content: "Most people spend their days in their heads—on screens, in thought, planning, doing. Subtle signals like tight jaw, clenched belly, shallow breathing go unnoticed. By the time we 'notice' stress, it's already showing up as fatigue, pain, burnout, inflammation, or illness."
        },
        {
          number: "4",
          title: "The 'I'm Fine' Reflex",
          content: "Our minds protect us from uncomfortable truths. If we believe we should be coping, the brain minimizes what we're feeling. We tell ourselves 'I'm fine' or 'I'll rest later'—even when our body is asking for relief."
        },
        {
          number: "5",
          title: "The Brave-Face Habit",
          content: "Many of us were raised to be strong, polite, or self-sufficient. We learned that showing emotion made others uncomfortable. Over time, keeping a brave face becomes automatic, and genuine self-awareness fades."
        },
        {
          number: "6",
          title: "Childhood Conditioning",
          content: "If we grew up where emotions were ignored or 'being tough' was expected, we learned to tune out our own distress. As adults, that conditioning still runs in the background. We don't feel safe admitting we're struggling, even to ourselves."
        },
        {
          number: "7",
          title: "Culture Rewards Disconnection",
          content: "Modern life praises busy, productive, and 'always on'—Hustle Culture! Rest is treated like a luxury vs a necessity. Workplaces and social media celebrate doing more—not feeling more."
        },
        {
          number: "8",
          title: "The Feedback Delay",
          content: "Today's stressors—finances, deadlines, relationships—never truly end. Because there's no clear 'danger over' moment, the body never resets. We only realize the impact much later: headaches, insomnia, digestive issues, anxiety, or burnout."
        }
      ]
    },
    bottomLine: {
      title: "The Bottom Line",
      icon: "logo",
      points: [
        "The body adapts to survive",
        "The mind protects the image of 'I'm fine'",
        "Society rewards disconnection from our inner world"
      ],
      insight: "Recognizing stress isn't weakness—it's wisdom. It's how you begin to work with your body instead of against it."
    },
    cortisolImpact: {
      title: "The Cortisol Connection",
      icon: "logo",
      content: "High stress creates high cortisol which affects sleep and all healthy hormones for anti-aging, inflammation reduction, healthy sleep, mood regulating hormones, cognitive efficiency—really everything good in our body. High cortisol 'depresses' all the good stuff. Puts a different spin on depression itself.",
      normalCurveImage: "https://customer-assets.emergentagent.com/job_a307acc3-a38c-406d-b0b9-bf89e6454073/artifacts/6q3bmbim_stress%20normal%20curve.png",
      normalCurveCaption: "Normal Anabolic/Catabolic Hormone Levels — In a healthy rhythm, cortisol peaks in the morning to get you going, then tapers through the day, allowing the body to enter repair mode at night.",
      exaggeratedImage: "https://customer-assets.emergentagent.com/job_a307acc3-a38c-406d-b0b9-bf89e6454073/artifacts/c9fw2s1j_stress%20exaggerated.png",
      exaggeratedCaption: "Exaggerated Stress Response — When stress becomes chronic, cortisol stays elevated all day and into the night, robbing the body of its critical repair windows for both physical and psychological recovery."
    },
    rebuildingAwareness: {
      title: "Rebuilding Stress Awareness: Gentle Remedies",
      icon: "logo",
      intro: "The good news: stress awareness can be re-learned. The nervous system is plastic. The body wants to communicate. We just need to reopen the channel.",
      remedies: [
        {
          number: "1",
          title: "Start With the Body, Not the Mind",
          content: "Instead of asking 'What's wrong with me?' try 'What am I sensing right now?' Notice breath depth, jaw tension, shoulders, belly, posture. Awareness alone begins to shift the stress response."
        },
        {
          number: "2",
          title: "Slow Something Down Daily",
          content: "Stress thrives on speed. Regulation thrives on rhythm. Choose one thing each day to do more slowly—walking, eating, breathing, transitioning between tasks. Slowness signals safety to the nervous system."
        },
        {
          number: "3",
          title: "Name What You Feel (Simply)",
          content: "You don't need perfect language. Words like tight, heavy, buzzy, tired, flat, or overfull are enough. Naming sensations reconnects the brain to the body."
        },
        {
          number: "4",
          title: "Normalize Rest Before Burnout",
          content: "Rest is not a reward for finishing everything. It's a biological requirement. Small moments—lying down, pausing, closing the eyes—retrain the system to recover before collapse."
        },
        {
          number: "5",
          title: "Practice Being Seen Without Performing",
          content: "Notice where you automatically 'put on a brave face.' Experiment with safe honesty—with yourself first, then with trusted others. Authenticity reduces stress faster than endurance ever will."
        },
        {
          number: "6",
          title: "Create Nervous System Rituals",
          content: "Gentle movement, breathwork, stretching, time in nature, or quiet body awareness aren't luxuries—they're regulatory tools. Consistency matters more than intensity."
        },
        {
          number: "7",
          title: "Patience Above All",
          content: "Rebuilding stress awareness isn't something to fix or optimize. It's a relationship you're restoring with your body. Progress often looks subtle—fewer reactions, a deeper breath, a moment of pause. There's a reason patience has a nickname... it's a Virtue."
        }
      ]
    },
    nervousSystemInfo: {
      title: "The Role of Stress on Your System",
      icon: "logo",
      content: "Stress and digestion are highly linked. The Parasympathetic (Rest/Digest) and Sympathetic (Fight/Flight) systems work like a see-saw—when one is working, the other is off.",
      goodNews: "1 hour in the Parasympathetic System can help restore balance from a heavy day—consider it close to 1 hr to 8 hrs ratio. Just 1 hr daily working in the calming PNS may make a world of difference!",
      stressFunnelImage: "https://customer-assets.emergentagent.com/job_a307acc3-a38c-406d-b0b9-bf89e6454073/artifacts/d2mmmhup_stress%20funnel.png",
      stressFunnelCaption: "The Stress Funnel — adapted from Paul Chek, CHEK Institute (2001)",
      stressFunnelContent: "We are not just dealing with one kind of stress. Physical, chemical, electromagnetic, psychic, nutritional, and thermal stressors all pour into the same system, filtered through our limbic, hormonal, visceral, nervous, and nutritional control systems. When the total load exceeds our capacity for homeostasis, the body begins to break down. Most of us are operating far closer to overflow than we realize — and adding more stress through intense exercise or rigid routines may be pushing us further from balance, not closer."
    },
    reframe: {
      title: "A Reframe to Carry With You",
      content: "Stress isn't a personal failure... It's a signal. And learning to listen is one of the most powerful forms of self-care."
    },
    books: [
      { title: "Mindfulness for Beginners", author: "Jon Kabat-Zinn" },
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "The Untethered Soul", author: "Michael Singer", note: "Best book for drawing attention to the voices in our heads" },
      { title: "Any book by Pema Chödrön", author: "", note: "Her book titles will guide you" }
    ],
    closingThought: "Stress isn't a personal failure—it's a signal. Learning to listen is one of the most powerful forms of self-care."
  },
  '9-pillars-wellness': {
    id: '9-pillars-wellness',
    title: 'The 9 Pillars for Creating a Path to Wellness',
    subtitle: 'Will You Give It A Year?',
    image: 'https://customer-assets.emergentagent.com/job_hello-starter-17/artifacts/c38gnnan_What-are-the-8-pillars-of-wellness-1024x585.webp',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-starter-17/artifacts/9j8za1l7_9%20Pillars%20Of%20%20Creating%20a%20Path%20to%20Wellness%20%281%29.pdf',
    wheelGraphic: 'https://customer-assets.emergentagent.com/job_hello-starter-17/artifacts/11ugf3od_wheel.png',
    introduction: "Most people don't struggle with wellness because they don't care. They struggle because they don't know where to begin. In a world saturated with advice, protocols, and trends, it's easy to bounce between ideas, attempt big changes, and then quietly burn out when life intervenes.\n\nThe result is often frustration, inconsistency, and the feeling that wellness is something we are chasing rather than living.",
    keyInsight: "I don't believe health is any one thing. I believe it is a living, shifting matrix.",
    matrixExamples: [
      "Sleep affects mood → Stress affects digestion → Thoughts influence choices",
      "Hydration impacts energy → Energy impacts the mind → Mind impacts emotions"
    ],
    pillarsIntro: {
      title: "The Purpose of the Pillars",
      icon: "logo",
      content: "The Pillars are not meant to be worked on all at once. They are meant to be noticed.\n\nEach pillar represents a foundational aspect of human well-being that influences how we feel, function, and move through the world.",
      question: "Rather than asking \"What should I fix?\" the Pillars invite a different question: \"Where is my system asking for support right now?\"",
      insight: "When the most strained areas are supported, the entire system begins to regulate."
    },
    sections: [
      {
        title: "1. Breathing & Being (Spiritual/Intuitive)",
        icon: "logo",
        content: "Breathing is the fastest doorway we have into regulation, presence, and awareness. It is not about technique first, but about relationship. How often do you pause? How often do you notice your body before reacting?",
        insight: "When breathing is shallow or absent, the nervous system stays on alert.",
        support: "Gentle breath awareness, pauses throughout the day, slowing transitions, practices that invite presence rather than performance.",
        keywords: "Breath • Pause • Presence"
      },
      {
        title: "2. Nourishment & Timing (Physical)",
        icon: "logo",
        content: "Food is not only fuel—it's information. What we eat, how often we eat, and when we eat all influence blood sugar, hormones, mood, focus, and energy. This pillar looks beyond rules and into rhythm.",
        insight: "Consistent and regular intervals of nourishment is one of the fastest ways to create a calmer nervous system. Think of the body a little like a 2-year-old. Toddlers thrive with structure.",
        support: "Stabilizing blood sugar, simplifying food choices, adjusting timing. Think fat, fiber and protein for a nice slow ride of insulin and blood sugar balance.",
        keywords: "Food • Blood Sugar • Rhythm"
      },
      {
        title: "3. Thoughts & Inner Dialogue (Mental)",
        icon: "logo",
        content: "The conversations we run internally shape how safe, capable, and motivated we feel. This pillar isn't about \"positive thinking,\" but about awareness.",
        insight: "Every thought we have is influencing every cell we have. We have trillions of cells, always listening, and morphing. In one year, that's 120 trillion new cells. If we change our minds, our frequencies, in 1 year you will have programmed the majority of you to be different.",
        support: "Awareness of self-talk, reframing patterns, curiosity over criticism. Curiosity is like instant electroshock therapy—it activates creative centers and draws electrical activity away from the Inner Critic zone.",
        keywords: "Mindset • Mood • Self-talk"
      },
      {
        title: "4. Hydration & Flow (Physical)",
        icon: "logo",
        content: "Hydration is more than water intake. It includes mineral balance, electrolytes, absorption, and flow—both physically and energetically.",
        insight: "When hydration is off, everything feels harder: digestion, sleep, focus, movement. We are shockingly low on minerals, and our food/farm fields just don't have it anymore.",
        support: "For hydration (mineral rich): sea moss, pink salt or high quality mine salt, silica. For detoxing (binders): Diatomaceous Earth (food grade), chlorella, charcoal, Zeolite.",
        keywords: "Water • Electrolytes • Fluidity"
      },
      {
        title: "5. Movement & Body Awareness (Physical)",
        icon: "logo",
        content: "Movement isn't about burning calories or chasing performance. It's about relationship—how you inhabit your body, how you move through space, and how safe movement feels.",
        insight: "This pillar includes stability, mobility, strength, and sensation. It also honors pain as information, not an enemy. When movement becomes respectful, freedom follows.",
        items: [
          { term: "1. Reduce or resolve pain", definition: "Safety, comfort, and nervous system trust come first" },
          { term: "2. Support endocrine and metabolic systems", definition: "Movement should not add physiological stress when the system is already taxed" },
          { term: "3. Restore functional movement patterns", definition: "Daily tasks feel easier, more efficient, and less effortful" },
          { term: "4. Build strength", definition: "Strength that supports stability, confidence, and resilience" },
          { term: "5. Develop power and endurance", definition: "Expression, performance, and capacity—when the foundation is ready" }
        ],
        support: "Gentle restorative movement, stability work, mindful strength, mobility, and listening to what the body is asking for.",
        keywords: "Movement • Pain • Freedom"
      },
      {
        title: "6. Sleep, Rest & Nervous System (Emotional)",
        icon: "logo",
        content: "Sleep is not just rest—it's repair, integration, and regulation. When sleep is disrupted, the nervous system often is too.",
        insight: "This pillar invites broader definitions of rest: nervous system recovery, intentional downtime, and practices that signal safety to the body. Rest is productive.",
        support: "Nervous system regulation, intentional downtime, sleep routines, and practices that signal safety to the body.",
        keywords: "Rest • Regulation • Recovery"
      },
      {
        title: "7. Emotional Awareness (Emotional)",
        icon: "logo",
        content: "Emotions are not problems to solve; they are signals to interpret. This pillar builds capacity to feel without becoming overwhelmed or disconnected.",
        insight: "Expression creates movement. Suppression creates stagnation. The goal is to free the body of stuck emotion—Energy In Motion. We rarely were taught to embrace these things called emotions.",
        support: "Emotional literacy, expression, boundaries, reflection, and creating space for feelings without judgment. Becoming aware especially of shame and fear and the power they can hold over us.",
        keywords: "Feelings • EQ • Expression"
      },
      {
        title: "8. Whole Self Integration (Spiritual)",
        icon: "logo",
        content: "This pillar speaks to alignment—the relationship between ego and soul, logic and intuition, effort and surrender.",
        insight: "Whole Self Integration is not about belief systems. It's about coherence within the self. When inner guidance is heard and respected, life feels less fractured and more intentional.",
        support: "Practices that foster alignment, reflection, intuition, meaning, and trust in one's inner compass.",
        keywords: "Ego • Soul • Inner Guidance"
      },
      {
        title: "9. The Self in Context",
        icon: "logo",
        content: "The final pillar is not something to \"work on\" directly. It's a culmination and a stacking of the other areas creating the FULL EXPRESSION OF YOU.",
        insight: "You are your parts. You will notice as you address the out of balance areas... the whole self-wellness, vitality changes state. When we enter a phase of life where we cannot address aspects of wellness, we are resilient to a point, only to sort of... fall apart."
      }
    ],
    threeInfluences: {
      title: "The Three Influences That Shape Every Pillar",
      icon: "logo",
      intro: "Wellness does not exist in a vacuum. This is where compassion and a true HOLISTIC view can be very beneficial and really very kind to self.",
      influences: [
        {
          name: "Hierarchy of Needs",
          description: "If basic safety, finances, housing, or rest are unstable, higher-level wellness behaviors become unrealistic. This isn't failure—it's physiology and psychology."
        },
        {
          name: "Life Circumstances",
          description: "Season of life matters. Caregiving, grief, transitions, menopause, injury, workload—these all shift capacity. The work adapts to the season, not the other way around."
        },
        {
          name: "Subconscious & Belief Systems",
          description: "Much of what drives behavior lives below awareness. Conditioning, early experiences, and inherited patterns quietly influence motivation, self-worth, and consistency. This is why effort alone doesn't create lasting change."
        }
      ],
      intentionNote: "INTENTION is 9/10th of the law! When we set intentions... everything else arranges itself to fall into alignment with the primary intention for our highest good."
    },
    oneYearJourney: {
      title: "How This Becomes a One-Year Journey",
      icon: "logo",
      question: "Are you willing to give it a year? This is TRULY how I changed my life... this! This process here. It's why I share it. It works.",
      content: "This framework isn't meant to be rushed—it is designed for your highest goals of longevity. Over a year, each pillar can be explored, revisited, and re-integrated as life evolves.",
      transformation: "The work becomes less about discipline and more about attunement. Less about fixing and more about listening. Less about pushing and more about stability and trust.",
      insight: "The smoothness of the wheel improves not when everything is perfect—but when the most strained areas are gently supported."
    },
    closingThought: "Life circumstances quietly shape everything in the background. When one area becomes strained, the others compensate... until they can't. This is where the Pillars come in. Not as rules. Not as goals. But as reference points. Places to gently bring attention when life feels bumpy, heavy, or out of sync."
  },

  'compassion-fatigue': {
    id: 'compassion-fatigue',
    title: 'Compassion Fatigue',
    subtitle: '"Where did I go?"',
    image: 'https://images.unsplash.com/photo-1616081440193-f49d7c56969f?w=800',
    opening: "When you stop feeling like yourself, there's a point where it's not just tired anymore. It's not just stress. Not just a busy week. It's that subtle, unsettling feeling of…\n\n\"Where did I go?\"",
    sections: [
      {
        id: 'not-caring-too-much',
        title: "It Doesn't Always Look Like \"Caring Too Much\"",
        type: 'intro-with-lists',
        intro: "While compassion fatigue is typically a coined term often used with health care practitioners, or any other caring field and especially motherhood/parenthood!!\n\nCompassion fatigue isn't always about giving to others.",
        listTitle: "Sometimes it looks like:",
        listItems: [
          "Not having the energy to care for yourself",
          "Avoiding the very things that used to support you",
          "Feeling disconnected from your own body",
          "Letting things slide… not because you don't know better",
          "But because something in you just… won't engage"
        ],
        maskTitle: "It can quietly wear the mask of:",
        maskItems: [
          "Low motivation",
          "Anxiety",
          "Apathy / stuck in a rut or stuck in a schedule",
          "Emotional numbness",
          "Even depression-like states"
        ],
        closing: "And that's where it gets confusing. Because it doesn't feel like \"compassion fatigue.\"\nIt just feels like you're off."
      },
      {
        id: 'whats-happening',
        title: "What's Actually Happening",
        type: 'nervous-system',
        intro: "At a nervous system level, this is less about emotion…\nand more about depletion.",
        triggerTitle: "When your system has been:",
        triggers: [
          "\"on\" for too long",
          "processing too much",
          "adapting, holding, managing"
        ],
        response: "…it starts to conserve.\nNot dramatically. But strategically.\n\nIt begins to dial things down.\nYour drive softens. Your spark dims. Your capacity to feel… narrows.",
        truth: "Not because you don't care. But because your system is quietly saying:\n\n\"We don't have the resources for this right now.\""
      },
      {
        id: 'no-one-talks',
        title: "The Part No One Talks About",
        type: 'turning-point',
        content: "This is the moment people often turn on themselves.",
        innerCritic: [
          "\"I'm being lazy.\"",
          "\"I should be doing more.\"",
          "\"What's wrong with me?\""
        ],
        pushback: "So they push. And the system… pushes back harder.",
        reframe: "What looks like lack of discipline\nis often lack of capacity."
      },
      {
        id: 'the-shift',
        title: "The Shift",
        type: 'shift',
        intro: "The way out isn't more pressure. It's not forcing motivation. Or \"getting your act together.\"\n\nIt's rebuilding safety and energy at the level the body understands.\n\nSmall things. Honest things.",
        practices: [
          "Sitting quietly instead of scrolling",
          "Breathing instead of bracing",
          "Moving gently instead of performing",
          "Letting your body lead for a moment"
        ],
        signal: "Not as a fix.\nBut as a signal:\n\n\"We're allowed to come back online.\""
      },
      {
        id: 'different-truth',
        title: "A Different Truth",
        type: 'truth',
        lines: [
          "You didn't lose your discipline.",
          "You didn't lose your edge.",
          "You didn't suddenly become unmotivated.",
          "You may have simply reached the point where:",
          "your system can no longer override itself."
        ]
      },
      {
        id: 'strong-opinions',
        title: "We Don't Have Time",
        type: 'passionate',
        content: "I have very strong opinions on this, which I mostly soften!\n\nBut we don't have time! People need to get healthy and we need to get healthy now.",
        realities: [
          "We are beat down, depressed, anxious, we are NOT OURSELVES.",
          "We MUST get stress out of the body!",
          "Being EXHAUSTED is being NORMALIZED! INFLUENCED even!!"
        ],
        insight: "We have this perception that stress lives out there in the details, in other people. But it lives in our processing or lack of processing of life, love and all things of this existence! And then it PARKS itself in our bodies!",
        consequences: "And we develop DIS-EASE, illness etc. Or general unwellness, or relationship problems, chronic pain."
      },
      {
        id: 'call-to-action',
        title: "Reconnect",
        type: 'cta',
        content: "If you want to FEEL BETTER and RECONNECT with your body and REIGNITE your SOUL CONNECTION... reach out for an introduction to new methods of moving forward in this new world."
      }
    ]
  },
  'our-relationship-with-anxiety': {
    id: 'our-relationship-with-anxiety',
    title: 'Our Relationship with Anxiety',
    subtitle: "It's Not In Your Head. It's In Your Body.",
    image: 'https://images.pexels.com/photos/4098224/pexels-photo-4098224.jpeg?w=800',
    opening: "Anxiety has become one of the most common experiences of modern life.\n\nYet many of us still misunderstand it.\n\nWhen someone says, \"It's just anxiety,\" it can feel dismissive. The heart is racing. The chest feels tight. The stomach is turning. The hands are sweaty. The body feels as though something dangerous is happening.\n\nAnd the truth is, something is happening. Just not necessarily what we think.",
    sections: [
      {
        id: 'what-is-my-body-telling-me',
        title: "What Is My Body Trying to Tell Me?",
        type: 'prose',
        paragraphs: [
          "Anxiety is not imaginary. It is not weakness. It is not a character flaw.",
          "It is a real physiological experience occurring within a very real human body.",
          "Perhaps the question is not \"What's wrong with me?\" Perhaps the better question is, \"What is my body trying to tell me?\""
        ],
        callout: "What's wrong with me?  →  What is my body trying to tell me?"
      },
      {
        id: 'living-through-an-experiment',
        title: "We Are Living Through an Experiment",
        type: 'prose',
        paragraphs: [
          "Human beings evolved over hundreds of thousands of years. The nervous system that carried our ancestors through storms, predators, childbirth, loss, uncertainty, and survival is essentially the same nervous system we carry today.",
          "What has changed is our environment. And it has changed faster in the last twenty years than perhaps any other period in human history.",
          "We now carry devices that connect us to the entire world. At any given moment we can receive messages from friends, family, strangers, businesses, governments, advertisers, news agencies, influencers, algorithms, and artificial intelligence. We are exposed to more information than any generation before us.",
          "Researchers call this information overload — a state where incoming information exceeds our ability to process it meaningfully. Younger people may be especially vulnerable, navigating constant digital input while their brains are still developing strategies to filter and prioritize.",
          "Our ancestors worried about the weather. We worry about the weather, politics, economic uncertainty, global conflicts, environmental concerns, social media comments, text messages, emails, notifications, and breaking news from every corner of the planet."
        ],
        callout: "The human nervous system was designed to respond to immediate threats.\nIt was not designed to carry the emotional weight of the entire world."
      },
      {
        id: 'faster-isnt-better',
        title: "Faster Isn't Always Better",
        type: 'prose',
        paragraphs: [
          "Technology promised us something remarkable: save time, increase efficiency, reduce effort. In theory, all that extra efficiency should have created more space for rest, reflection, creativity, connection, and presence.",
          "Instead, many of us filled the space with more tasks. More notifications. More content. More pressure. More urgency.",
          "Artificial intelligence can process information at extraordinary speed. Computers can perform millions of calculations in seconds. But human beings are not computers. We are biological organisms."
        ],
        listTitle: "Every one of these unfolds according to biological rhythms that cannot be rushed:",
        listItems: [
          "We breathe",
          "We digest",
          "We circulate blood",
          "We repair tissues and create new cells",
          "We sleep, we dream, we heal"
        ],
        callout: "The body still operates at human speed. The world does not. And many nervous systems are paying the price."
      },
      {
        id: 'maybe-its-energy',
        title: "Maybe It Isn't Anxiety. Maybe It's Energy.",
        type: 'nervous-system',
        intro: "What if we temporarily set aside the word anxiety?\nWhat if we called it energy?",
        triggerTitle: "When we become anxious, we lose connection to sensation — yet the body keeps speaking:",
        triggers: [
          "The chest tightens",
          "The jaw clenches",
          "The shoulders rise",
          "The breath becomes shallow",
          "The heart beats faster"
        ],
        response: "The body is communicating.\n\nThe mind is interpreting.\n\nThese are not always the same thing.",
        truth: "Energy moving through the body.\n\nEnergy searching for expression. Energy asking for attention."
      },
      {
        id: 'inquiry-changes-everything',
        title: "Inquiry Changes Everything",
        type: 'prose',
        paragraphs: [
          "One of the fastest ways to interrupt anxiety is through inquiry. Not analysis. Inquiry.",
          "Instead of asking \"What's wrong with me?\" — get curious about the sensation itself."
        ],
        listTitle: "Try asking:",
        listItems: [
          "Where do I feel this in my body?",
          "What shape does it have?",
          "Is it moving or still?",
          "Is it hot or cold? Tight or loose?",
          "What happens if I simply notice it?"
        ],
        callout: "The moment we become curious, the nervous system begins to soften.\nWe shift from fear to observation. From resistance to awareness. From panic to presence."
      },
      {
        id: 'downshifting',
        title: "The Missing Ingredient: Downshifting",
        type: 'prose',
        paragraphs: [
          "Many people spend most of their day in higher-frequency thinking states — planning, solving, reacting, scrolling, consuming, responding. The nervous system rarely receives a signal that it is safe enough to slow down.",
          "Yet healing, recovery, digestion, immune function, hormone balance, tissue repair, learning, creativity, and emotional processing all thrive when the body can downshift.",
          "We are becoming highly stimulated and poorly recovered. Highly connected and poorly grounded. Highly informed and poorly integrated."
        ]
      },
      {
        id: 'quick-ways-to-downshift',
        title: "Quick Ways to Downshift",
        type: 'shift',
        intro: "When anxiety or excess energy is present, these simple practices remind your nervous system that you are here, now:",
        practices: [
          "Lengthen the exhale",
          "Feel your feet on the ground",
          "Orient to your environment — look around the room",
          "Hum, sing, or gargle",
          "One hand on the chest, one on the abdomen",
          "Walk slowly",
          "Reduce incoming information for a few minutes",
          "Step outside and expose your eyes to natural light",
          "Focus on sensation rather than story"
        ],
        signal: "None of these techniques fix your life.\n\nThey simply remind your nervous system that you are here, now. And that matters."
      },
      {
        id: 'long-term-solutions',
        title: "Long-Term Solutions",
        type: 'prose',
        paragraphs: [
          "There is no breathing exercise powerful enough to offset a lifestyle that continually overwhelms the nervous system.",
          "Long-term change requires a relationship. A relationship with the body. With rest. With movement. With emotion. With attention.",
          "Practices such as restorative movement, mindfulness, meditation, breathwork, nature exposure, journaling, emotional processing, meaningful relationships, and conscious technology use help build a nervous system that is more resilient to modern life."
        ],
        callout: "The goal is not to eliminate anxiety. The goal is to increase capacity — to become a person who can experience energy without becoming consumed by it."
      },
      {
        id: 'a-final-thought',
        title: "A Final Thought",
        type: 'truth',
        lines: [
          "Perhaps anxiety is not always evidence that something is wrong.",
          "Sometimes it is evidence that something is asking for our attention.",
          "The body is remarkably intelligent. It speaks through sensation long before it speaks through illness.",
          "When we learn to listen, we often discover that anxiety was never our enemy.",
          "It was a messenger — trying to bring us back home to ourselves."
        ]
      },
      {
        id: 'call-to-action',
        title: "Come Back Home to Yourself",
        type: 'cta',
        content: "If anxiety has been a loud messenger in your life, you don't have to navigate it alone. Reach out to explore restorative movement, breathwork, and nervous-system support designed for real life in this modern world."
      }
    ]
  },
  'why-anxiety-creates-symptoms': {
    id: 'why-anxiety-creates-symptoms',
    title: 'Why Anxiety Creates Symptoms in the Body',
    subtitle: "It's Not 'Just a Thought.' It's a Biochemical Event.",
    image: 'https://images.pexels.com/photos/3812752/pexels-photo-3812752.jpeg?w=800',
    opening: "Anxiety is not \"just a thought.\"\n\nIt is a biochemical event.\n\nWhen your brain perceives threat — whether a real emergency, a frightening thought, a text message, a memory, a deadline, or a future fear — the body activates its stress response.",
    sections: [
      {
        id: 'it-begins-in-the-brain',
        title: "It Begins in the Brain",
        type: 'prose',
        paragraphs: [
          "The amygdala — the brain's threat detector — sends an alarm signal. The hypothalamus then activates two major systems: the sympathetic nervous system and the HPA axis (hypothalamic-pituitary-adrenal axis). This is where the cascade begins.",
          "The sympathetic nervous system triggers the adrenal glands to release adrenaline and noradrenaline. These chemicals act quickly — they increase heart rate, raise blood pressure, sharpen alertness, shift blood flow toward the muscles, speed up breathing, and slow down digestion. This is the classic fight-or-flight response.",
          "At the same time, the HPA axis activates a slower hormonal chain. The hypothalamus releases CRH, which signals the pituitary to release ACTH, which tells the adrenal cortex to release cortisol — the body's primary stress hormone. Cortisol raises blood glucose so the body has fast fuel available for survival."
        ]
      },
      {
        id: 'these-symptoms-are-real',
        title: "These Symptoms Are Real",
        type: 'prose',
        paragraphs: [
          "So when someone says, \"My heart is racing,\" they are not imagining it — their heart may actually be beating faster because adrenaline is telling it to pump harder.",
          "\"I feel shaky\" — stress hormones are mobilizing glucose and energy.",
          "\"I feel sick to my stomach\" — digestion has been temporarily downregulated while the body prioritizes survival.",
          "\"I can't breathe\" — breathing has become faster and more shallow, which can create dizziness, tingling, chest tightness, and a terrifying sense of air hunger."
        ],
        callout: "These symptoms are real. They are not made up. They are not weakness. They are not drama.\nThey are physiology."
      },
      {
        id: 'the-hormonal-cascade',
        title: "The Hormonal Cascade of Anxiety",
        type: 'prose',
        paragraphs: ["Here is the simplified version:"],
        listItems: [
          "Step 1 — The brain detects threat. It may be physical, emotional, imagined, remembered, digital, relational, or internal.",
          "Step 2 — The amygdala sounds the alarm. This part of the brain is constantly scanning for danger.",
          "Step 3 — The hypothalamus activates survival mode, signaling the autonomic nervous system and the HPA axis.",
          "Step 4 — Adrenaline rises. Heart rate and blood pressure climb, breathing speeds up, blood moves toward the muscles. The body prepares to fight, flee, freeze, or protect.",
          "Step 5 — Cortisol rises. It raises blood sugar and keeps the body alert. In small bursts this is adaptive; over time, repeated activation becomes costly.",
          "Step 6 — The body changes priorities. Digestion slows, reproductive and immune activity shift, muscles tighten, sleep lightens, the brain grows more vigilant.",
          "Step 7 — The person feels symptoms. The body feels loud, weird, intense, and frightening.",
          "Step 8 — The mind creates a story: \"Something is wrong with my heart.\" \"I'm going to pass out.\" \"I can't breathe.\" \"I'm dying.\""
        ],
        callout: "And now the loop begins. The fear of the symptoms becomes another threat. The brain detects more danger. More adrenaline. More cortisol. More symptoms. More fear.\nThis is how anxiety becomes a self-feeding loop."
      },
      {
        id: 'why-the-heart',
        title: "Why the Heart Gets Involved",
        type: 'prose',
        paragraphs: [
          "The heart is one of the first organs affected by the stress response. Adrenaline tells it to beat faster and stronger so blood can move quickly to the muscles. You may feel pounding, fluttering, tightness, or a surge in your chest.",
          "This can feel like a heart problem. And of course, new, severe, or unusual chest pain should always be medically assessed.",
          "But in many anxiety and panic episodes, the heart is not failing. The heart is responding — following the command of the stress chemistry."
        ]
      },
      {
        id: 'why-breathing',
        title: "Why Breathing Feels Strange",
        type: 'prose',
        paragraphs: [
          "During stress, breathing often becomes faster, higher, and more chest-based to prepare the body for action.",
          "But if you are sitting still while your body is preparing to run, the chemistry has nowhere to go. Fast breathing can change carbon dioxide levels in the blood, contributing to dizziness, tingling, numbness, chest tightness, and feeling disconnected from the body.",
          "This is why anxiety can feel so dramatic — the breathing pattern itself can intensify the symptoms."
        ]
      },
      {
        id: 'why-digestion',
        title: "Why Digestion Shuts Down",
        type: 'prose',
        paragraphs: [
          "The body does not prioritize digestion during perceived danger. If the nervous system believes survival is at stake, it redirects energy away from the gut and toward the heart, lungs, brain, and muscles.",
          "This can show up as nausea, diarrhea, constipation, bloating, reflux, stomach pain, appetite changes, or that classic \"pit in the stomach.\"",
          "The gut is not separate from anxiety. It is deeply wired into the stress response."
        ]
      },
      {
        id: 'why-muscles-tighten',
        title: "Why Muscles Tighten",
        type: 'nervous-system',
        intro: "Muscles tighten because the body is preparing for action. This is not random — this is bracing.",
        triggerTitle: "A body that feels unsafe becomes a body that guards:",
        triggers: [
          "The jaw clenches",
          "The shoulders rise",
          "The chest tightens",
          "The hips grip",
          "The pelvic floor contracts",
          "The breath becomes restricted"
        ],
        response: "When this happens repeatedly, the body can begin to live in a low-grade protective posture.",
        truth: "This is where anxiety, pain, stiffness, fatigue, and nervous-system dysregulation often start dancing together in the same dark little ballroom."
      },
      {
        id: 'the-point',
        title: "The Point We Need to Understand",
        type: 'truth',
        lines: [
          "Anxiety symptoms are not imaginary. They are not \"just in your head.\"",
          "They are the visible effects of an invisible chemical cascade.",
          "The heart responds. The lungs respond. The gut responds. The muscles respond. The brain responds. The whole system responds.",
          "The real question is not simply, \"Do I have anxiety?\"",
          "The better question is: \"Why does my body keep believing I am unsafe?\" That question opens the door to real healing."
        ]
      },
      {
        id: 'safety-first',
        title: "A Note on Safety First",
        type: 'prose',
        paragraphs: [
          "Know the difference between real body symptoms that need immediate medical attention and ones that can be managed on your own. It is always best to be cleared medically if you are truly concerned.",
          "These days, the number of people showing up in the ER with anxiety-related heart symptoms (and others) is astounding. Once you are cleared, you can get to work rewriting your programming — knowing you are all clear.",
          "Use that situation as wisdom for the next time. Follow your gut guidance. We know… we have just forgotten that we know."
        ],
        callout: "This article is for education and is not medical advice. When in doubt, get checked — then do the deeper work."
      },
      {
        id: 'call-to-action',
        title: "Rewrite the Programming",
        type: 'cta',
        content: "If your body keeps believing it is unsafe, you don't have to stay stuck in the loop. Reach out to explore restorative movement, breathwork, and nervous-system support that helps your body learn safety again."
      }
    ],
    relatedResource: {
      label: 'Continue Exploring',
      title: 'Introduction to Metaphysics',
      blurb: "If anxiety is the body speaking, metaphysics is learning its language. When the body speaks through sensation, tension or illness — the most powerful thing we can do is listen.",
      to: '/resources/whole-self?resource=intro-to-metaphysics',
    }
  },
  'systemic-fungal-infections': {
    id: 'systemic-fungal-infections',
    title: 'Systemic Fungal Infections: A Holistic Guide',
    subtitle: 'A Whole-Person Look at Yeast, Fungi & the Body\u2019s Terrain',
    image: 'https://images.unsplash.com/photo-1780253481026-951abd1c164f?w=800',
    opening: "Systemic fungal infections occur when yeast or fungi move beyond localized areas \u2014 like skin, nails, or the gut \u2014 and begin affecting the body more broadly.\n\nWhile Candida albicans is the most common, other fungi (Aspergillus, Cryptococcus, Histoplasma, and others) can also contribute.",
    sections: [
      {
        id: 'signs-symptoms',
        title: 'Signs & Symptoms',
        type: 'prose',
        paragraphs: [
          "Systemic fungal overgrowth can show up in obvious ways \u2014 and in subtler patterns that traditional Chinese Medicine has long recognized."
        ],
        groups: [
          {
            title: 'Obvious / Common (Western clinical perspective)',
            tone: 'caution',
            items: [
              "Chronic fatigue or a \u201Ccrash\u201D after meals",
              "Recurrent infections (sinus, vaginal, urinary, oral thrush)",
              "Gastrointestinal upset \u2014 bloating, diarrhea, constipation, gas",
              "Skin rashes, itching, eczema-like eruptions, fungal nails",
              "Sugar and carb cravings",
              "Brain fog, poor memory, difficulty concentrating",
              "Persistent cough or shortness of breath (if the lungs are involved)",
              "Fever and chills (in acute systemic infection)"
            ]
          },
          {
            title: 'Less Obvious / Subtle (often recognized in Chinese Medicine)',
            tone: 'tcm',
            items: [
              "Dampness and phlegm signs: a heavy body, sluggishness, a muzzy head",
              "White, thick, greasy tongue coating",
              "Low appetite with distension after eating",
              "Loose or sticky stools with a foul odor",
              "Recurrent vaginal discharge with damp-heat patterns",
              "\u201CCloudy spirit\u201D: mental dullness, low motivation, difficulty connecting",
              "Cold-damp presentation: clammy skin, heaviness in the limbs",
              "Heat-damp presentation: irritability, foul-smelling discharges, red skin eruptions"
            ]
          }
        ]
      },
      {
        id: 'possible-causes',
        title: 'Possible Causes',
        type: 'prose',
        paragraphs: [
          "Fungal overgrowth rarely has a single cause. It tends to take hold when the body\u2019s terrain \u2014 its inner environment \u2014 becomes hospitable to it."
        ],
        groups: [
          {
            title: 'Western / Biomedical',
            tone: 'neutral',
            items: [
              "Prolonged or repeated antibiotic use (disrupts gut flora)",
              "High-sugar, high-refined-carb diets",
              "Immunosuppression (chemotherapy, HIV/AIDS, chronic illness, corticosteroid use)",
              "Diabetes (especially when poorly controlled)",
              "Long-term stress \u2192 adrenal dysregulation \u2192 lowered immunity",
              "Environmental exposure (mold, damp living spaces)"
            ]
          },
          {
            title: 'Holistic / Lifestyle',
            tone: 'default',
            items: [
              "Chronic stress and poor sleep (weakens host resistance)",
              "Excess alcohol intake",
              "Overuse of oral contraceptives or hormonal medications",
              "Heavy metal burden or toxic load (fungi thrive in a toxic terrain)",
              "Sedentary lifestyle (stagnation = internal dampness)"
            ]
          },
          {
            title: 'Chinese Medicine',
            tone: 'tcm',
            items: [
              "Spleen Qi deficiency \u2192 failure to transform dampness \u2192 accumulation",
              "Liver Qi stagnation \u2192 transforms to damp-heat \u2192 fungal flare",
              "Kidney Yang deficiency \u2192 chronic, lingering infections",
              "External damp invasion (living in humid or moldy conditions)"
            ]
          }
        ]
      },
      {
        id: 'conventional-treatments',
        title: 'Possible Treatments',
        type: 'prose',
        paragraphs: [
          "Conventional medicine offers important tools, especially for severe or systemic infections. These are best directed and monitored by your physician."
        ],
        listTitle: 'Conventional / Medical Approaches',
        listItems: [
          "Antifungal medications: fluconazole, itraconazole, amphotericin B (for severe infections)",
          "Topical antifungals (if skin or nails are involved)",
          "IV antifungals for life-threatening systemic infections",
          "Monitoring underlying conditions (e.g., diabetes management, immune function)"
        ]
      },
      {
        id: 'holistic-support',
        title: 'Holistic & Integrative Support',
        type: 'prose',
        paragraphs: [
          "Alongside any medical care, supporting the terrain helps the body become less hospitable to fungal overgrowth \u2014 and more resilient overall."
        ],
        groups: [
          {
            title: 'Nutrition & Lifestyle',
            tone: 'default',
            items: [
              "Eliminate or drastically reduce: sugar, refined carbs, alcohol, yeasted breads, dairy (if mucus-forming)",
              "Non-starchy vegetables: broccoli, leafy greens, zucchini, cauliflower",
              "Clean proteins: wild fish, organic poultry, legumes if tolerated",
              "Low-glycemic fruits: berries, green apples",
              "Fermented foods (sauerkraut, kimchi) if tolerated \u2014 but avoid if they cause flare-ups",
              "Healthy fats: olive oil, coconut oil, avocado",
              "Adequate hydration with lemon water and herbal teas",
              "Regular movement to reduce stagnation and dampness"
            ]
          },
          {
            title: 'Supplements & Botanicals (evidence-based + traditional)',
            tone: 'default',
            items: [
              "Probiotics (multi-strain, high CFU)",
              "Caprylic acid (from coconut oil)",
              "Berberine (goldenseal, Oregon grape)",
              "Oregano oil (a strong antifungal \u2014 use carefully)",
              "Garlic / allicin",
              "Pau d\u2019Arco tea",
              "Grapefruit seed extract",
              "Digestive enzymes to reduce biofilm and improve gut terrain"
            ]
          },
          {
            title: 'Detox / Terrain Support',
            tone: 'neutral',
            items: [
              "Support the liver with milk thistle, dandelion root, burdock",
              "Sweat therapies (infrared sauna, gentle exercise)",
              "Ensure regular bowel movements for toxin clearance"
            ]
          }
        ]
      },
      {
        id: 'chinese-medicine',
        title: 'Chinese Medicine Approaches',
        type: 'prose',
        paragraphs: [
          "Chinese Medicine views fungal overgrowth as a terrain problem \u2014 dampness that needs to be transformed and organs that need to be strengthened. Work with a qualified practitioner before using herbal formulas."
        ],
        groups: [
          {
            title: 'Herbal Formulas',
            tone: 'tcm',
            items: [
              "Shen Ling Bai Zhu San \u2014 for Spleen Qi deficiency with damp",
              "Long Dan Xie Gan Tang \u2014 for Liver/Gallbladder damp-heat",
              "Er Chen Tang \u2014 for phlegm-damp accumulation"
            ]
          },
          {
            title: 'Acupuncture & Diet Therapy',
            tone: 'tcm',
            items: [
              "Acupuncture to strengthen Spleen & Kidney, clear damp-heat, and regulate Qi flow",
              "Avoid damp-forming foods: dairy, fried, greasy, and overly sweet foods",
              "Favor lightly cooked veggies, warming spices (ginger, cinnamon if no heat signs), and bitter greens"
            ]
          }
        ]
      },
      {
        id: 'mind-body',
        title: 'Lifestyle & Mind-Body Practices',
        type: 'prose',
        paragraphs: [
          "Fungi thrive on chaos. A steady rhythm and a regulated nervous system are quietly powerful medicine."
        ],
        listItems: [
          "Prioritize restorative sleep (immune resilience)",
          "Practice stress reduction: meditation, Qi Gong, yoga, breathwork",
          "Regularly air out living spaces; consider mold remediation if necessary",
          "Maintain a balanced daily rhythm \u2014 fungi thrive on chaos (irregular eating, sleeping, overwork)",
          "Journaling and emotional support: fungal overgrowth is often linked to unprocessed grief, resentment, or stuck emotions in the TCM Liver/Spleen axis"
        ],
        callout: "This article is for education and is not medical advice. Systemic fungal infections can be serious \u2014 always work with a qualified healthcare provider for diagnosis and treatment, then layer in the terrain and lifestyle support that helps you heal."
      },
      {
        id: 'call-to-action',
        title: 'Tend to Your Terrain',
        type: 'cta',
        content: "Healing fungal overgrowth is really about restoring balance \u2014 in the gut, the immune system, and the whole body. If you\u2019d like support building a terrain that no longer feeds the problem, reach out to explore a personalized, whole-self approach."
      }
    ]
  },
  'escaping-the-dopamine-cycle': {
    id: 'escaping-the-dopamine-cycle',
    title: 'Escaping the Dopamine Cycle',
    subtitle: 'Why Intention Matters More Than Motivation',
    image: 'https://images.pexels.com/photos/16345434/pexels-photo-16345434.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    opening: "We hear a lot about dopamine these days. Dopamine addiction. Dopamine detox. Everyone is chasing dopamine.\n\nThe problem is that most of these conversations oversimplify what dopamine actually is.\n\nDopamine is not the pleasure chemical. It's the pursuit chemical.",
    sections: [
      {
        id: 'pursuit-chemical',
        title: 'The Pursuit Chemical',
        type: 'prose',
        paragraphs: [
          "Dopamine is the neurochemical of anticipation, seeking, curiosity, motivation, novelty, and reward prediction. In other words, dopamine is what gets us moving toward something.",
          "The challenge is that modern life has become a dopamine buffet. Our brains evolved in a world where rewards were relatively scarce and required effort. Today, rewards arrive instantly."
        ],
        listTitle: "Instant rewards, everywhere:",
        listItems: [
          "A notification", "A text", "A reel", "A purchase", "A news headline", "A like", "A snack", "Another episode", "Another scroll"
        ],
        callout: "The brain doesn't know the difference between hunting for food on the savannah and hunting for the next interesting thing on a screen. It simply responds to the signal — and that signal is getting louder every year."
      },
      {
        id: 'the-dopamine-loop',
        title: 'The Dopamine Loop',
        type: 'prose',
        paragraphs: [
          "The anticipation is often stronger than the reward itself. You check your phone. Your brain predicts something interesting might be there. Dopamine rises. You find something mildly rewarding. Dopamine drops. Your brain immediately begins searching for the next hit. Repeat. Again. And again.",
          "The result is that many people spend their day chasing tiny bursts of stimulation while feeling increasingly dissatisfied. Not because they lack pleasure — because they lack fulfillment.",
          "The nervous system becomes conditioned to seek instead of experience."
        ],
        callout: "Anticipation → Reward → Temporary Satisfaction → Drop → Craving → Repeat"
      },
      {
        id: 'in-the-brain',
        title: "What's Happening in the Brain?",
        type: 'prose',
        paragraphs: [
          "When dopamine repeatedly spikes, the brain adapts. This is normal biology — the brain constantly tries to maintain balance. Scientists call this homeostasis."
        ],
        groups: [
          {
            title: 'Over time, repeated high-dopamine stimulation can lead to:',
            tone: 'caution',
            items: [
              "Reduced sensitivity to rewards",
              "Increased need for novelty",
              "Shorter attention span",
              "More impulsive behavior",
              "Reduced motivation for slower rewards",
              "Difficulty feeling satisfied"
            ]
          },
          {
            title: 'Meaningful rewards are slower — and nourishing:',
            tone: 'default',
            items: [
              "Learning a skill",
              "Improving your health",
              "Building relationships",
              "Creating something",
              "Healing",
              "Exercising",
              "Reading",
              "Growing a business"
            ]
          }
        ],
        callout: "These slower rewards don't provide immediate fireworks — they provide steady nourishment. Reaching for the quick hit instead is the neurological equivalent of eating candy when your body needs a proper meal."
      },
      {
        id: 'why-we-get-stuck',
        title: 'Why We Get Stuck',
        type: 'prose',
        paragraphs: [
          "The dopamine cycle isn't usually about weakness. It's often about discomfort. Many of us are unconsciously trying to regulate something underneath."
        ],
        listTitle: "Often we're quietly trying to regulate:",
        listItems: [
          "Stress", "Anxiety", "Loneliness", "Uncertainty", "Boredom", "Emotional pain", "Fatigue"
        ],
        callout: "The brain discovers that scrolling, snacking, shopping, gambling, binge-watching, or constant busyness temporarily changes how we feel. But because the root cause remains, the brain asks for another hit. Soon the reward becomes less about pleasure and more about relief."
      },
      {
        id: 'intention',
        title: 'Intention: The Missing Ingredient',
        type: 'truth',
        lines: [
          "Not motivation. Not discipline. Not willpower. Intention.",
          "Motivation is emotional — it comes and goes. Willpower gets tired.",
          "Intention is a conscious decision about who you are becoming.",
          "Without intention, the brain defaults to immediate gratification. With intention, it begins evaluating choices differently.",
          "The question shifts from \"What do I feel like doing?\" to \"What serves where I'm going?\""
        ]
      },
      {
        id: 'interrupt-the-cycle',
        title: 'How To Interrupt The Cycle',
        type: 'prose',
        paragraphs: [
          "You don't need to wage war on your brain. You need a few small, repeatable shifts."
        ],
        groups: [
          {
            title: '1. Create Awareness Before Action',
            tone: 'neutral',
            items: [
              "Most dopamine loops are automatic. Pause.",
              "Before reaching for your phone, snack, purchase, or distraction, ask: What am I feeling right now? Stress? Fatigue? Boredom? Avoidance?",
              "Awareness breaks autopilot."
            ]
          },
          {
            title: '2. Increase Friction',
            tone: 'neutral',
            items: [
              "The brain loves convenience — so make impulsive behaviors slightly harder.",
              "Turn off notifications. Remove apps from the home screen. Keep tempting foods out of sight.",
              "Even small obstacles can dramatically reduce automatic behavior."
            ]
          },
          {
            title: '3. Practice Delayed Gratification',
            tone: 'neutral',
            items: [
              "You don't need to eliminate pleasure — you simply need to regain choice.",
              "Wait ten minutes. Take a walk first. Complete one task first. Pause before responding to every urge.",
              "This strengthens the brain's executive control systems."
            ]
          },
          {
            title: '4. Pursue Meaningful Dopamine',
            tone: 'neutral',
            items: [
              "Not all dopamine is created equal: creating vs. consuming, building vs. scrolling, learning vs. distracting, connecting vs. comparing.",
              "The goal isn't less dopamine. The goal is better dopamine."
            ]
          },
          {
            title: '5. Let Yourself Be Bored',
            tone: 'neutral',
            items: [
              "Boredom is often where creativity, insight, and self-awareness begin.",
              "The moment discomfort appears, we fill it — yet the nervous system needs quiet space to reset.",
              "Some of life's best ideas arrive in the gaps."
            ]
          }
        ]
      },
      {
        id: 'nervous-system-connection',
        title: 'The Nervous System Connection',
        type: 'prose',
        paragraphs: [
          "Many people think they have a dopamine problem when they actually have a nervous system problem. A stressed nervous system seeks relief. A regulated nervous system can tolerate stillness."
        ],
        listTitle: "This is why these practices can feel surprisingly powerful:",
        listItems: [
          "Walking", "Breathwork", "Meditation", "Restorative movement", "Nature exposure", "Meaningful conversation", "Creative expression"
        ],
        callout: "They reduce the need to constantly seek stimulation. The goal isn't becoming a monk. The goal is becoming free enough to choose."
      },
      {
        id: 'the-real-goal',
        title: 'The Real Goal',
        type: 'truth',
        lines: [
          "Most people think they want more dopamine. What they actually want is more vitality — more energy, focus, purpose, connection, and peace.",
          "The answer isn't eliminating pleasure. It's remembering that pleasure and fulfillment are not the same thing.",
          "A life built around dopamine asks: \"What feels good right now?\"",
          "A life built around intention asks: \"What creates the life I actually want?\"",
          "One creates temporary stimulation. The other creates transformation — and transformation almost always begins with intention."
        ]
      },
      {
        id: 'call-to-action',
        title: 'Begin With Intention',
        type: 'cta',
        content: "If you're ready to step out of the cycle of seeking and into a life of intention, reach out. Together we can build the nervous-system regulation and daily rhythm that make better dopamine — and real fulfillment — possible."
      }
    ]
  },
  'i-gotta-get-my-shift-together': {
    id: 'i-gotta-get-my-shift-together',
    title: 'I Gotta Get My Shift Together',
    subtitle: 'Understanding the System That Created Your Current Life',
    image: 'https://images.unsplash.com/photo-1701634441311-e624c7fdfedf?w=800',
    opening: "Growth isn't just effort. It's identity disruption.\n\nBecause if you grow… you outgrow the version of you that felt familiar. And the nervous system doesn't always celebrate that. It resists — not because growth is wrong, but because unfamiliar feels unsafe.",
    sections: [
      {
        id: 'opening-practices',
        title: 'Two Opening Practices',
        type: 'prose',
        paragraphs: [
          'We begin not with a plan, but with a felt sense. Two simple practices to soften the nervous system and open the door to what you actually want.'
        ],
        groups: [
          {
            title: '1. Intention',
            tone: 'default',
            items: [
              'Draw a circle and place everything you wish for yourself inside the circle.',
              "It's great to separate your wishes into BIG aims and SMALL aims.",
            ],
          },
          {
            title: '2. Flow — the water glasses',
            tone: 'neutral',
            items: [
              'Set out two water glasses. One represents now and the past; the other represents the new and the future.',
              'Pour water into the past/now glass. Pause and feel.',
              'Look at the new/future glass. Pause and feel.',
              'When ready, pour the water from glass one into glass two. Pause and feel.',
            ],
          },
        ],
      },
      {
        id: 'roving-priorities',
        title: 'Roving Priorities & Energy Alignment',
        type: 'prose',
        paragraphs: [
          'Working with myself and others, an all-too-common challenge — and source of great frustration — is the sense of making whichever state or activity we are currently living in the number-one, top priority all the time.',
          'So when we are at work, that is number one. Then we go to the gym, and we judge our bodies for not being in the shape we wish them to be. We meet with friends or family for a night on the town and suddenly our social life is the priority — forgetting we have work in the morning and the health goals we set 30 minutes ago. This is literally impossible, unrealistic, and may cause a great deal of stress as well as a sense of failure.',
          'If you recall Winnie the Pooh at all, you may remember the Bisy Backson — a little-known character, roughly translated to "Busy… Back Soon." Bisy was an exhaustingly hyper character, easily looped into doing everything and anything, who felt he/she was missing out if Bisy wasn\'t up to something. We really could learn a great deal about ourselves through the characters of Winnie the Pooh. This is certainly not everyone — but perhaps some of us.'
        ],
      },
      {
        id: 'priorities-exercise',
        title: 'Exercise: Rank Your Priorities',
        type: 'prose',
        paragraphs: [
          'Rank these categories and sub-categories in order of priority — and create your own as required. This exercise will reflect your life and priorities, and help you discover how your energy is aligned with them.',
          'These are the main areas of life. Use mine at first, then tailor them to your life. Interchange sub-categories and categories — for example, I have "career" under Financial, but perhaps career needs to be its own category for you. I suggest using a pencil, because you will be erasing and rewriting many times.'
        ],
        listTitle: 'The main areas of life:',
        listItems: [
          'Health — fitness, emotional health, stress reduction (or this can all go under one umbrella)',
          'Wealth / Financial — earnings, savings, spending, etc.',
          'Social — family of creation, family of origin, friends',
          'Playtime, hobbies, fun — whatever works',
          'Relationships — significant other, close friends, parents, children, boss, etc.',
          'Personal space',
          'Contribution, spirituality, volunteering, etc.',
        ],
      },
      {
        id: 'before-momentum',
        title: "Before Momentum… There's Pattern",
        type: 'prose',
        paragraphs: [
          "We like to think we're making conscious choices. But most of us are running a pattern. A system. A kind of internal operating code built from early experiences, emotional moments, and decisions we made in seconds… that lasted decades.",
          'Tony shares a story from when he was 11. There was no food at Thanksgiving because his father was unemployed. Stress filled the home — and then someone dropped off dinner.',
          'His father made it mean: "This is charity. I\'m worthless." And he left. Tony made it mean: "Strangers care." That one meaning turned into a lifetime of contribution… feeding millions of people.',
          'Same event. Different meaning → different identity → different life.'
        ],
      },
      {
        id: 'the-question',
        title: 'The Question Most People Never Ask',
        type: 'truth',
        lines: [
          'Not these: What do I want? Why am I stuck?',
          'But these: What shaped me?',
          "What did I decide back then… that I'm still living out now?",
          'Because those decisions — they become your default map.'
        ],
      },
      {
        id: 'lead-system',
        title: "Your 'Lead System': The Invisible Steering Wheel",
        type: 'prose',
        paragraphs: [
          "We all share the same six core needs. But here's the twist: you don't prioritize them equally. One or two tend to run the show. This is your lead system."
        ],
        listTitle: 'It quietly determines:',
        listItems: [
          'Your decisions',
          'Your habits',
          'Your relationships',
          'Your risks',
          "Your version of 'success'",
        ],
      },
      {
        id: 'same-needs',
        title: 'Same Needs, Different Lives',
        type: 'prose',
        paragraphs: [
          'Two people both crave significance.'
        ],
        groups: [
          {
            title: 'One becomes…',
            tone: 'default',
            items: ['A firefighter', 'A leader', 'Someone who protects and serves'],
          },
          {
            title: 'Another…',
            tone: 'caution',
            items: ['Seeks control through fear', 'Uses violence', 'Or lives in chronic conflict'],
          },
        ],
        callout: 'Same need. Different map.'
      },
      {
        id: 'certainty-uncertainty',
        title: 'Certainty & Uncertainty: The Built-In Paradox',
        type: 'prose',
        groups: [
          {
            title: 'Certainty',
            tone: 'neutral',
            items: [
              "We all need certainty — safety, comfort, knowing we'll be okay. Without it, nothing else matters.",
              "If your world feels unstable, you're not thinking about growth, purpose, or contribution. You're thinking: 'Am I okay?'",
            ],
          },
          {
            title: 'Uncertainty',
            tone: 'default',
            items: [
              "Here's the twist: if you had total certainty, you'd be bored out of your mind. Predictable everything = emotional flatline.",
              'We crave surprise, change, the unknown.',
              "We love the surprises we want… and call the ones we don't 'problems.' But both are doing the same thing: injecting life back into the system.",
            ],
          },
        ],
      },
      {
        id: 'how-we-meet-needs',
        title: 'How We Meet Our Needs (Consciously or Not)',
        type: 'prose',
        paragraphs: [
          "This part is subtle… but massive. We don't just have needs — we have strategies for meeting them. And not all strategies are ideal."
        ],
        groups: [
          {
            title: 'Certainty — Healthy',
            tone: 'default',
            items: ['Building a skill', 'Creating stability'],
          },
          {
            title: 'Certainty — Unhealthy',
            tone: 'caution',
            items: ['Controlling everything', 'Giving up entirely'],
          },
          {
            title: 'Significance — Healthy',
            tone: 'default',
            items: ['Growth and mastery'],
          },
          {
            title: 'Significance — Unhealthy',
            tone: 'caution',
            items: ['Comparison and conflict', 'Even destruction'],
          },
        ],
        callout: 'The need itself is not the problem. The strategy is everything.'
      },
      {
        id: 'first-four-last-two',
        title: 'The First Four vs The Last Two',
        type: 'prose',
        groups: [
          {
            title: 'The First Four — Personality Needs',
            tone: 'neutral',
            items: ['Certainty', 'Uncertainty', 'Significance', 'Love / Connection', '"You will meet these no matter what. Even if you have to lie to yourself to do it."'],
          },
          {
            title: 'The Last Two — Spiritual Needs',
            tone: 'default',
            items: ['Growth', 'Contribution', 'This is where fulfillment lives — not happiness, not comfort, but fulfillment. Without these, you can have money, relationships, success… and still feel like something is quietly missing.'],
          },
        ],
      },
      {
        id: 'growth-feels-hard',
        title: 'The Real Reason Growth Feels Hard',
        type: 'prose',
        paragraphs: [
          "Growth isn't just effort. It's identity disruption. Because if you grow, you outgrow the version of you that felt familiar.",
          "And the nervous system doesn't always celebrate that. It resists — not because growth is wrong, but because unfamiliar feels unsafe."
        ],
      },
      {
        id: 'meaning-movement',
        title: 'The Secret Thread: Meaning → Movement',
        type: 'prose',
        paragraphs: [
          "Every experience you've had, you gave it meaning. That meaning shaped your beliefs, your identity, your map.",
          'And now? That map is either creating momentum… or quietly looping you in place.'
        ],
      },
      {
        id: 'forward-momentum',
        title: "Forward Momentum Isn't About More Effort",
        type: 'prose',
        paragraphs: [
          "It's about alignment between three things:"
        ],
        groups: [
          {
            title: '1. Your Target',
            tone: 'neutral',
            items: ["Do you actually know where you're going?"],
          },
          {
            title: '2. Your Map',
            tone: 'neutral',
            items: ['Is your strategy moving you forward… or keeping you safe?'],
          },
          {
            title: '3. Your Lead System',
            tone: 'neutral',
            items: [
              'Which needs are driving you the most right now?',
              'Are you chasing certainty?',
              'Craving significance?',
              'Avoiding uncertainty?',
              'Starving for connection?',
            ],
          },
        ],
        callout: 'You are always moving. The question is… is it forward… or just familiar?'
      },
      {
        id: 'quiet-reframe',
        title: 'A Quiet Reframe to Sit With',
        type: 'prose',
        paragraphs: [
          "If something in your life isn't moving, instead of asking 'Why can't I change?', try asking:"
        ],
        listItems: [
          'What need am I trying to meet right now?',
          'What strategy am I using to meet it?',
          'Where did I learn this strategy?',
          'And… is it still serving me?',
        ],
        callout: "Forward momentum doesn't come from forcing yourself into a new life. It comes from understanding the system that created your current one… and gently, consciously, choosing a new direction. Not by becoming someone else — but by becoming more aware of who's been driving all along."
      },
      {
        id: 'twelve-core-systems',
        title: 'What Shaped Me? Core Driving / Lead Systems',
        type: 'prose',
        paragraphs: [
          'Beneath our strategies often sits a "false core" — an old, untrue belief — and the "false self" we built to compensate for it. See which ones feel familiar.'
        ],
        listTitle: 'The False Core → The False Self',
        listItems: [
          'There is something wrong with me / I am imperfect → I must be perfect',
          'I am worthless / of no value → I must prove my value',
          'I cannot do → I must overachieve (the over-doer)',
          'I am inadequate → I must prove adequacy',
          "I don't exist → I must prove I exist",
          'I am alone → I must prove connection (the over-connector)',
          'I am incomplete → I must become complete (the over-experiencer)',
          'I am powerless → I must become powerful',
          'I am loveless → I must become lovable',
          "I am 'out of control' → I must gain control",
          'I am crazy → I must get healthy',
        ],
      },
      {
        id: 'call-to-action',
        title: 'Begin With Awareness',
        type: 'cta',
        content: "You don't have to overhaul your whole life tonight. Start by noticing the system that's been running. Awareness is the first shift — and from there, small, conscious choices create real forward momentum. If you'd like support on this path, reach out — let's get your shift together."
      }
    ]
  }
};
